<template>
  <div :id="component_id" class="message" :class="[{'current-user-msg-container':is_current_user_message}, {'user-specific-message':message.userSpecific === currentUserGuid}, {'tac-chat-intro-close':(is_tacchat_intro || is_tacchat_close)}, {'activity-line':is_activity_message}]">
    <!-- Message Content Section -->
    <div class="flex message-container">
      <div v-if="!is_tacchat_intro && !is_tacchat_close && !is_activity_message && (is_ai_message || !threadIsSystemAi)" class="avatar-container mt-auto" :class="is_current_user_message?'order-2 ml-2':'order-0 mr-2'">
        <messenger-avatar :key="`avatar_${message.id}`" :size="32" :image-src="getSenderImage(message.senderId)" :image-zoom="1" :bg-color="$FUSfusionAvatarColorsHex[senderIndex]" :name="senderName" :icon="senderIcon" :monochrome-icon="sender_image_is_glyph" />
        <div v-if="!is_ai_message" class="justify-start text-text-subtle text-xs font-normal pt-1">&nbsp;</div>
      </div>
      <div v-if="is_ai_message" class="avery-message spark-msg self-stretch mb-0 p-4 rounded-lg outline outline-1 outline-offset-[-1px] outline-transparent inline-flex flex-col justify-start items-start gap-2.5 relative" :class="ai_message_class">
        <!-- Verifying facts spinner — full-width bar, pushes content down -->
        <div v-if="isVerifying" class="w-full flex justify-end items-center gap-1.5 px-2 py-1 mb-1 text-text-subtle text-xs font-medium">
          <svg class="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Finalizing Answer
        </div>
        <!-- An action Rey asked to take and cannot take by itself: the buttons are the only route to
             performing it, over an authenticated endpoint the agent loop cannot reach. Handled here
             rather than in splitMessageByPatterns below, because an AI message never reaches that
             splitter — it goes through renderAveryMarkdown, which would print the card's JSON at the
             user as literal text. -->
        <!-- The header band of a scheduled result, in the Scheduled tasks thread. Placed BEFORE the
             confirmation-card/body pair rather than wrapping it, so the v-if/v-else chain below stays
             intact — and because the band is chrome about the message, not a replacement for it. It
             renders only where the task id is present AND the thread is the scheduled one: the same
             result sitting in someone's main Rey thread from before the move is an ordinary message
             there, and dressing it as a card would claim a thread it is not in. -->
        <!-- The origin banner of a forked thread. Above the result card branch and independent of it:
             this message IS the seeded copy, so it renders the banner and then its own body as an
             ordinary Rey message. It deliberately does NOT get a result card — the copy is a snapshot,
             and the task's live record stays in the Scheduled tasks thread. -->
        <scheduled-fork-banner v-if="scheduled_fork"
                               :key="`scheduled_fork_${message.id}`"
                               :from-thread-id="scheduled_fork.fromThreadId"
                               :run-at="scheduled_fork.runAt"
                               @openOrigin="$emit('openScheduledThread', $event)" />
        <scheduled-result-card v-if="scheduled_result"
                               :key="`scheduled_card_${message.id}`"
                               :task-name="scheduled_result.taskName"
                               :run-at="scheduled_result.runAt"
                               :outcome="scheduled_result.outcome"
                               :dealer-id="scheduled_result.dealerId"
                               :headline="scheduled_result.headline"
                               :metrics="scheduled_result.metrics"
                               :definition="scheduled_result.definition"
                               :sources="scheduled_result.sources" />
        <!-- One morning-brief finding. Mutually exclusive with the result card by thread rather than by
             branch: the two gates read different thread flags, and no thread is both. -->
        <brief-finding-card v-if="brief_finding"
                            :key="`brief_card_${message.id}`"
                            :finding-key="brief_finding.findingKey"
                            :domain="brief_finding.domain"
                            :status="brief_finding.status"
                            :produced-at="brief_finding.producedAt"
                            :dealer-id="brief_finding.dealerId"
                            :metrics="brief_finding.metrics"
                            :definition="brief_finding.definition"
                            :scope="brief_finding.scope" />
        <confirmation-card v-if="confirmation_request"
                           :key="`confirm_${message.id}`"
                           :decision-id="confirmation_request.Id"
                           :actionable="confirmation_is_live"
                           :title="confirmation_request.Title"
                           :summary="confirmation_request.Summary"
                           :confirm-label="confirmation_request.ConfirmLabel"
                           :cancel-label="confirmation_request.CancelLabel" />
        <div v-else class="self-stretch justify-start text-text-default/80 text-base font-normal leading-5 tracking-tight" :class="streamingDisplayBuffer?'streamed-message':''"
             v-show="streamingDisplayBuffer || message_content" v-html="streamingDisplayBuffer?renderAveryMarkdown(streaming_buffer_displayed, message.id):renderAveryMarkdown(message_content, message.id)" />
        <!-- Charts rendered from persisted_charts data property, not chart_artifacts computed.
             This prevents Vue from removing the canvas when the message object is replaced
             (e.g., by FactualityVerified or processStreamMessage mapping). -->
        <div v-for="artifact in rendered_charts" :key="artifact.id"
             :id="`chart-container-${artifact.id}`"
             style="display: none; width: 100%; height: 400px; position: relative;">
          <canvas :id="artifact.id"></canvas>
        </div>
        <!-- Every table in this answer, one worksheet each. Only offered for two or more — a single table
             already has its own download button. Inside the bubble so it sits at the answer's bottom-right
             corner; the feedback row below is as wide as the suggestion chips, not the answer. Same
             "finished typing" gate as that row: an export mid-stream would capture a half-written table. -->
        <div v-if="!isStreamingMessage && (!streamingDisplayBuffer || (streamingDisplayBuffer.isComplete && !is_typing)) && exportable_table_count >= 2"
             class="self-stretch flex justify-end">
          <button type="button"
                  class="download-all-tables-btn inline-flex items-center"
                  :class="tables_download_processing ? 'opacity-25 cursor-wait' : 'cursor-pointer hover:text-text-link'"
                  :disabled="tables_download_processing"
                  :title="$t('messaging.downloadAllTables')" :aria-label="$t('messaging.downloadAllTables')"
                  @click="downloadAllTables">
            <i class="ri-file-excel-2-line ri-100" />
          </button>
        </div>
        <!-- The conversational action from the result card's spec'd footer, placed here rather than on the
             card itself so it sits with the other footer controls (copy, feedback, Sources) instead of up
             in the header band. Same gate as the adjust control: only a result, only in its own thread. -->
        <!-- A scheduled result ONLY. A brief finding was offered this too — the design has any brief card
             breakable out into its own thread, and the server still accepts both sources — but forking one
             sends the reader somewhere else at the exact moment they got interested, and a brief is a
             morning's reading in one place. The finding's follow-up belongs in the brief thread, which is
             what the control below does instead. -->
        <!-- Labelled, unlike the glyphs in the icon row under the card, on purpose: it is the only control
             on a result that starts a conversation, and as a bare glyph at the end of that row it went
             unnoticed. A labelled control reads as a different KIND of thing from copy and feedback, which
             is exactly what this one should do.
             It has since moved INTO the card, which reverses the placement reasoning two comments up: as
             the card's own footer, ruled off the way the header band is, it reads as part of the result
             rather than as one more utility beside copy and feedback. self-stretch because the card lays
             its children out at their own width, and the rule has to span it. The tooltip still names
             where it goes.
             One footer for both card types — a scheduled result's and a brief finding's — because they
             never share a thread, and one frame means the two cannot drift into different shapes. -->
        <div v-if="scheduled_result || (brief_finding_chattable && brief_chat_prompt)"
             class="self-stretch pt-2.5 border-t border-border-separator flex justify-start" data-test="card-chat-footer">
          <button v-if="scheduled_result" type="button" data-test="scheduled-chat-about"
                  :class="card_chat_button_class"
                  :title="$t('messaging.scheduledResult.openInOwnThread')"
                  @click="$emit('forkScheduledResult', message.id)">
            <i class="ri-question-answer-line ri-100" aria-hidden="true" />
            {{ $t('messaging.scheduledResult.chatAbout') }}
          </button>
          <!-- Morning brief only. Writes a starter question into the composer below rather than sending
               one: the finding's own sentence arrives already quoted, and the person edits or adds to it
               before asking. Nothing is said on their behalf.
               A labelled button in the card's own footer, the same as a scheduled result's. It used to be
               an icon in the row under the card, kept that way because a labelled control among glyphs
               reads as a different kind of thing — but that consistency cost it the one thing it needs,
               which is to be noticed: it is the invitation to ask about the finding, not a utility on the
               message. In the card it reads as part of the finding.
               A quote bubble because the action QUOTES: it drops the finding's own sentence into the
               composer between quote marks, and does not send it. Distinct from a scheduled result's
               button in this same footer, which forks to a new topic and carries its own glyph.
               Still a GATHERED finding only, the same test the fork applied: a domain whose data could not
               be read states a fetch failure rather than a fact about the store, so there is nothing to
               talk about, and that also excludes the card an empty brief posts. -->
          <button v-if="brief_finding_chattable && brief_chat_prompt" type="button" data-test="brief-chat-about"
                  :class="card_chat_button_class"
                  :title="$t('messaging.briefFinding.chatAbout')"
                  @click="$emit('chatAboutFinding', brief_chat_prompt, brief_chat_scope)">
            <i class="ri-chat-quote-line ri-100" aria-hidden="true" />
            {{ $t('messaging.briefFinding.chatAbout') }}
          </button>
        </div>
      </div>
      <div v-else-if="is_tacchat_intro" class="self-stretch pl-6 pr-4 py-4 outline outline-1 outline-offset-[-1px] outline-brand-outline inline-flex justify-start items-center gap-4 overflow-hidden w-full">
        <div class="w-16 h-16 flex justify-center items-center gap-2">
          <img :src="tac_chat_logo" :alt="$t('messaging.tacChat')" class="w-full h-full rounded-full object-cover" />
        </div>
        <div class="flex-1 self-stretch p-2.5 flex justify-start items-start gap-2.5 overflow-hidden">
          <div class="justify-start">
            <span class="text-base font-bold leading-5">{{ tac_chat_agent_name }}</span>
            <br />
            <span class="text-sm leading-4 tracking-wide">{{ tac_chat_agent_title }} has joined the chat</span>
          </div>
        </div>
      </div>
      <div v-else-if="is_tacchat_close" class="self-stretch text-center justify-center text-text-default text-sm font-medium w-full leading-4 tracking-tight">
        {{ message.content }}
        <br /><br />
        You can start a new chat at any time.
      </div>
      <div v-else-if="is_tacchat_message">
        <div class="avery-message tac-msg text-gray-50 self-stretch mb-0 p-4 rounded-lg outline outline-1 outline-offset-[-1px] outline-transparent inline-flex flex-col justify-start items-start gap-2.5 bg-gray-800">
          <!-- Image attachment -->
          <div v-if="image_attachment_urls" class="w-full">
            <a v-for="(imgUrl, imgIndex) in image_attachment_urls" :key="`tacchat_img_${message.id}_${imgIndex}`" :href="safeUrl(imgUrl)" target="_blank" rel="noopener noreferrer" class="block mb-2">
              <img :src="safeUrl(imgUrl)" class="max-w-full max-h-[300px] object-contain rounded" :alt="$t('messaging.imageAttachment')" />
            </a>
          </div>
          <!-- Regular text content -->
          <div v-else class="self-stretch justify-start tracking-tight" :class="streamingDisplayBuffer?'streamed-message':''"
               v-show="streamingDisplayBuffer || message_content" v-html="streamingDisplayBuffer?renderAveryMarkdown(streaming_buffer_displayed, message.id):renderAveryMarkdown(message_content, message.id)" />
        </div>
        <message-timestamp :key="`${component_key}_timestamp_tacchat`" :message="message" :users-by-guid="usersByGuid" :is-current-user-message="is_current_user_message" :tac-chat-data="tac_chat_data" />
      </div>
      <!-- A scheduled run the gates held back. Rendered inside the ACTIVITY branch rather than beside it
           because that is what it is: the message is typed `activity`, which already keeps it out of the
           unread count and out of anything reading the thread for results. It gets a card instead of the
           centered line below because it has to name a task and, for a deferral, a time — which a
           one-line join/left treatment has nowhere to put.
           Gated on the activity metadata, so an ordinary join/left line (which carries none) keeps the
           plain rendering it has always had and this card can never capture one it knows nothing about. -->
      <scheduled-activity-card v-else-if="is_activity_message && scheduled_activity"
                               :key="`scheduled_activity_${message.id}`"
                               :kind="scheduled_activity.kind"
                               :deferred-until="scheduled_activity.deferredUntil">
        {{ message.content }}
      </scheduled-activity-card>
      <div v-else-if="is_activity_message" class="w-full text-center text-text-subtle text-xs py-1">
        {{ message.content }}
      </div>
      <!-- min-w-0: this column is a flex child of .message-container, and a flex child will not shrink
           below its own min-content width unless told to. Ordinary text wraps, so its min-content is one
           word and the default never showed — but an unbreakable file name made min-content the whole
           name, and the column grew to 911px inside a 424px container, dragging the attachment card off
           the side of the chat. With min-w-0 the column obeys the container and the card can truncate. -->
      <div v-else class="flex-1 order-1 mb-0 pl-3 min-w-0">
        <div class="message-content" :class="messageReply && !threadIsSystemAi?[ai_message_class, replyPublished?'message-published':'message-unpublished']:''">
          <template v-for="(splitMessage, index) in splitMessageByPatterns(message_content)">
            <span v-if="splitMessage.type==='tag'" class="tagged-user">{{ formattedMessage(splitMessage.text) }}</span>
            <span v-else-if="splitMessage.type==='emoji' && findEmoji(splitMessage.text)" class="emoji-message" :style="`background-position: ${findEmoji(splitMessage.text).getPosition()}`" />
            <br v-else-if="splitMessage.type==='br'" />
            <template v-else-if="splitMessage.type==='text'">{{ splitMessage.text }}</template>
            <div v-else-if="splitMessage.type==='json'" class="p-2 border border-border-separator w-full">
              <div class="flex flex-col items-start">
                <div v-if="splitMessage.object.thumbnail" class="text-center flex-shrink-0 mb-2" :style="{ marginBottom: '10px', marginRight: '0' }">
                  <img :src="splitMessage.object.thumbnail" class="img-fluid" alt="thumbnail" style="height: 75px; object-fit: cover;">
                </div>
                <div class="flex-grow mb-2" style="text-align: left">
                  <div class="font-bold">
                    {{ splitMessage.object.year }} {{ splitMessage.object.make }} {{ splitMessage.object.model }} {{ splitMessage.object.trim }}
                  </div>
                  <div>Mileage: {{ splitMessage.object.mileage }}</div>
                  <div>Color: {{ splitMessage.object.exteriorColor }}</div>
                  <div>{{ $t('messaging.dealerLabel') }} <span class="cursor-pointer text-brand" @click="openAppraisal(splitMessage.object)">{{ splitMessage.object.dealerName }}</span></div>
                </div>
                <div class="flex flex-col w-full">
                  <fusion-dropdown :key="'dealer_picker_' + message.id + '_' + index" class="mb-1" :placeholder="$t('messaging.chooseDealer')"
                                   :options="dealer_roofs_array" label-key="label" value-key="value"
                                   :model-value="dealer_for_viewing_appraisal[message.id]"
                                   @update:model-value="dealerGuid => dealer_for_viewing_appraisal[message.id] = dealerGuid" />
                  <button class="inline-block text-center text-[var(--text-button-primary)] bg-btn-primary hover:opacity-90 px-3 py-1.5 cursor-pointer disabled:opacity-65 rounded-none w-full"
                          :disabled="!dealer_for_viewing_appraisal[message.id]"
                          @click="openAppraisal(splitMessage.object, message.id)">{{ $t('messaging.view') }}</button>
                </div>
              </div>
            </div>
            <!-- Three columns in normal flow: thumbnail, name, actions. The actions used to be
                 position:absolute at the top-right, which meant a long file name ran underneath them
                 instead of stopping — "inventory-aging-2026-07-30.xlsx" collided with both icons. In flow
                 the name simply cannot reach them: it truncates and keeps its full text on hover. -->
            <div v-else-if="splitMessage.type==='file'" class="p-2 border border-border-separator flex items-center justify-start gap-2" style="height: 90px">
              <div v-if="attachment_errors.indexOf(`file_${message.id}_${index}`)>=0" class="text-center shrink-0" style="width: 72px">
                <i class="ri-attachment-2 ri-2x text-text-placeholder hover:text-text-subtle cursor-pointer" />
              </div>
              <image-container v-else :key="`file_${message.id}_${index}`" :image-src="safeUrl(splitMessage.object.URL)" image-dimension="1x1" class="shrink-0" style="max-width: 72px" @errorLoading="attachment_errors.push(`file_${message.id}_${index}`)" />
              <!-- min-w-0 is what lets truncate work at all: without it a flex child refuses to shrink
                   below its content width and the overflow reappears. -->
              <div class="flex-1 min-w-0">
                <p class="mb-1 font-bold truncate" :title="splitMessage.object.Filename">{{ splitMessage.object.Filename }}</p>
                <p class="mb-1 text-text-placeholder hidden"><small>{{ splitMessage.object }}</small></p>
              </div>
              <div class="flex items-center gap-3 shrink-0 self-start">
                <!-- Keep this file for the whole store. Next to Download because both act on the file
                     itself, and unlike a prompt shown after upload it is still here when someone realises
                     the document is worth keeping. Renders nothing when there is nothing to offer. -->
                <promote-attachment-button v-if="thread_id_for_promotion && splitMessage.object.Filename"
                                           :thread-id="thread_id_for_promotion"
                                           :file-name="splitMessage.object.Filename" />
                <a :href="safeUrl(splitMessage.object.URL)" target="_blank" rel="noopener noreferrer" :title="$t('messaging.download')" @click.stop="" download>
                  <i class="ri-download-cloud-fill ri-150 text-text-link hover:text-text-link" />
                </a>
              </div>
            </div>
            <div v-else-if="splitMessage.type==='image'" class="p-2 w-full">
              <a v-for="(imgUrl, imgIndex) in splitMessage.object.urls" :key="`img_${message.id}_${index}_${imgIndex}`"
                 :href="safeUrl(imgUrl)" target="_blank" rel="noopener noreferrer" class="block mb-2">
                <img :src="safeUrl(imgUrl)" class="max-w-full max-h-[300px] object-contain rounded" :alt="$t('messaging.imageAttachment')" />
              </a>
            </div>
          </template>
          <div v-if="message.userSpecific === currentUserGuid && !threadIsSystemAi && messageReply" class="user-specific-message-response flex flex-col">
            <div class="self-stretch p-0.5 inline-flex justify-start items-start gap-4">
              <div class="flex-1 justify-start text-text-default/80 text-base font-normal leading-5 tracking-tight" v-html="renderPublishedMessage(message.id)" />
              <div class="p-1 opacity-70 flex justify-start items-center gap-2.5">
                <span v-tooltip="private_message_tooltip"><i class="cursor-pointer ri-information-line ri-xl" /></span>
              </div>
            </div>
            <template v-if="messageReply.content && !replyPublished">
              <div class="self-stretch py-4 inline-flex justify-end items-start gap-3 flex-wrap content-start">
                <div class="h-10 rounded-3xl outline outline-1 outline-offset-[-0.50px] outline-border-default inline-flex flex-col justify-center items-center gap-2 cursor-pointer" @click="publishMessage(message.threadId, messageReply.id)">
                  <div class="px-6 py-2.5 inline-flex justify-center items-center gap-2">
                    <div class="text-center justify-center text-text-subtle text-sm font-medium leading-4 tracking-tight">{{ $t('messaging.publish') }}</div>
                  </div>
                </div>
                <div class="h-10 rounded-3xl inline-flex flex-col justify-start items-start gap-2">
                  <div class="self-stretch h-10 bg-btn-secondary rounded-3xl flex flex-col justify-center items-center gap-2 cursor-pointer" @click="replyToMessage">
                    <div class="px-6 py-2.5 inline-flex justify-center items-center gap-2">
                      <div class="text-center justify-center text-text-subtle text-sm font-medium leading-4 tracking-tight">{{ $t('messaging.reply') }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <div class="self-stretch mt-2 inline-flex justify-start items-center gap-3 overflow-hidden">
              <i :class="['ri-file-copy-line ri-100 cursor-pointer hover:text-text-link', copy_success ? 'opacity-25' : '']" :title="$t('messaging.copyMessage')" @click="copyMessageToClipboard('reply')" />
              <i v-if="false && messageCancelled" class="ri-loader-4-line ri-100 cursor-pointer hover:text-text-link" />
              <i v-if="!messageCancelled" class="ri-100" :class="messageFeedbackStatus===true?'ri-thumb-up-line':(messageFeedbackStatus==='ThumbsUp'?'ri-thumb-up-fill text-text-link':'ri-thumb-up-line cursor-pointer hover:text-text-link')" :title="$t('messaging.feedback.markHelpful')" @click="submitFeedback(message.id, 0)" />
              <i v-if="!messageCancelled" class="ri-100" :class="messageFeedbackStatus===true?'ri-thumb-down-line':(messageFeedbackStatus==='ThumbsDown'?'ri-thumb-down-fill text-text-link':'ri-thumb-down-line cursor-pointer hover:text-text-link')" :title="$t('messaging.feedback.markUnhelpful')" @click="submitFeedback(message.id, 1)" />
              <i class="ri-volume-up-line ri-100 cursor-pointer hover:text-text-link" :title="$t('messaging.readAloud')"/>
              <tool-citations v-if="$FUSarrayHasEntries(toolCitations)" :key="`tool_citations_${message.id}`" :citations-list="toolCitations" />
            </div>
          </div>
        </div>
        <message-timestamp v-if="message_content && !threadIsSystemAi" :key="`${component_key}_timestamp_standard`" :message="message" :users-by-guid="usersByGuid" :is-current-user-message="is_current_user_message" />
      </div>
    </div>
    <!-- end Message Content Section -->
    <!-- AI Message Feedback Section -->
    <div v-if="is_ai_message && !isStreamingMessage && (!streamingDisplayBuffer || (streamingDisplayBuffer.isComplete && !is_typing))"
         class="self-stretch mt-2 pl-10 inline-flex justify-start items-center gap-3 overflow-hidden">
      <i :class="['ri-file-copy-line ri-100 cursor-pointer hover:text-text-link', copy_success ? 'opacity-25' : '']" :title="$t('messaging.copyMessage')" @click="copyMessageToClipboard" />
      <i v-if="false && messageCancelled" class="ri-loader-4-line ri-100 cursor-pointer hover:text-text-link" />
      <i v-if="!messageCancelled" class="ri-100" :class="messageFeedbackStatus===true?'ri-thumb-up-line':(messageFeedbackStatus==='ThumbsUp'?'ri-thumb-up-fill text-text-link':'ri-thumb-up-line cursor-pointer hover:text-text-link')" :title="$t('messaging.feedback.markHelpful')" @click="submitFeedback(message.id, 0)" />
      <i v-if="!messageCancelled" class="ri-100" :class="messageFeedbackStatus===true?'ri-thumb-down-line':(messageFeedbackStatus==='ThumbsDown'?'ri-thumb-down-fill text-text-link':'ri-thumb-down-line cursor-pointer hover:text-text-link')" :title="$t('messaging.feedback.markUnhelpful')" @click="submitFeedback(message.id, 1)" />
      <i class="ri-volume-up-line ri-100 cursor-pointer hover:text-text-link" :title="$t('messaging.readAloud')" @click="$emit('speakMessage', message.id)" />
      <!-- NPM-13: only on a delivered scheduled result, which is what carries the task id. Clicking it
           anchors the NEXT message to this task, so the person can write "shorter, just gross and units"
           and have it recorded as feedback instead of answered as a question. The anchor is why no phrase
           list is needed anywhere: an ordinary message carries nothing and is treated as ordinary. -->
      <!-- `scheduled_result`, not `scheduled_task_id`: the id is carried by a FORK's seed too (it is a
           copy of a result, and reading it still counts as engaging with the task), so gating on the id
           alone put this control inside forked threads — offering to adjust how future runs word
           themselves, in a thread that exists to work on one past result. `scheduled_result` is the
           narrower question, "is this a result IN the scheduled thread", which is what this means. -->
      <i v-if="scheduled_result" class="ri-edit-line ri-100 cursor-pointer hover:text-text-link"
         :title="$t('messaging.scheduledResult.adjust')"
         @click="$emit('adjustScheduledTask', scheduled_task_id)" />
      <tool-citations v-if="$FUSarrayHasEntries(toolCitations)" :key="`tool_citations_${message.id}`" :citations-list="toolCitations" />
    </div>
    <!-- end AI Message Feedback Section -->
    <!-- AI Message Feedback Comment — visible only when this AI message has a thumbs-down. Toggle-to-open,
         submitted explicitly via the "Submit comment" button or Enter. The 100-char cap is enforced both
         natively (maxlength) and visualized via the counter so the user sees how close they are. -->
    <div v-if="is_ai_message && messageFeedbackStatus === 'ThumbsDown' && !isStreamingMessage" class="self-stretch mt-2 pl-10 flex flex-col gap-1">
      <!-- Transient confirmation that fades after submit. The thank-you is surfaced by submitComment() when a
           save actually occurred during the edit session (tracked via comment_saved_since_open). Rendered
           OUTSIDE the open/closed branch so it persists above the "Edit comment" link until its 3s timer
           expires. -->
      <transition
        enter-active-class="transition-opacity duration-200"
        leave-active-class="transition-opacity duration-700"
        enter-from-class="opacity-0"
        leave-to-class="opacity-0">
        <div v-if="comment_thank_you_visible" class="text-[10px] font-medium text-green-600">
          Thank you for your feedback.
        </div>
      </transition>
      <button v-if="!comment_box_open" type="button"
              class="text-text-link text-sm font-medium cursor-pointer hover:underline self-start"
              @click="toggleCommentBox">
        {{ messageComment ? $t('messaging.feedback.editComment') : $t('messaging.feedback.addComment') }}
      </button>
      <div v-else class="flex flex-col gap-1">
        <!-- Colors are intentionally hardcoded here (bg-white + text-black) instead of the codebase's
             preferred theme tokens, per explicit product direction — comment text must always read as
             black, so the background is pinned to white to guarantee contrast in both light and dark themes. -->
        <textarea ref="comment_input" v-model="comment_draft" maxlength="100" rows="1"
                  :placeholder="$t('messaging.feedback.commentPlaceholder')"
                  class="w-full p-2 text-sm rounded border border-border-separator bg-white text-black focus:outline-none focus:border-text-link"
                  @keydown.enter.prevent="submitComment" />
        <!-- Counter + submit control share the row below the textarea. Submit persists any change via
             submitCommentIfChanged, then collapses the editor back to the "Edit comment" link state. The
             thank-you confirmation (rendered above this branch) carries across the collapse. -->
        <div class="flex items-center justify-between">
          <button type="button" class="text-xs text-text-link cursor-pointer hover:underline" @click="submitComment">
            Submit comment
          </button>
          <div class="text-xs text-text-subtle">{{ comment_draft.length }}/100</div>
        </div>
      </div>
    </div>
    <!-- end AI Message Feedback Comment -->
    <!-- AI Message Suggestions -->
    <!-- `isLatestAiMessage || scheduled_awaiting`: an awaiting question keeps its suggested answers
         however many runs post after it. The spec is explicit that "a result waiting on an answer is
         never removed or superseded by a later run" — and the latest-message gate alone silently broke
         that, because the chips vanished the moment the next morning's result arrived, leaving a card
         that still asks but no longer offers a way to reply. -->
    <div v-if="(isLatestAiMessage || scheduled_awaiting) && $FUSarrayHasEntries(message.suggestions) && !isStreamingMessage && (!streamingDisplayBuffer || (streamingDisplayBuffer.isComplete && !is_typing))" class="avery-suggestions pb-1 mt-2">
      <div v-for="(suggestion, suggestionIndex) in message.suggestions" :key="`suggestion_${message.id}_${suggestionIndex}`" class="w-full flex justify-end py-1">
        <button class="outline outline-1 outline-offset-[-1px] outline-brand-outline text-text-link text-[14px] hover:bg-brand-subtle rounded-[14px] py-2 px-3" :disabled="!messageSendReady" @click="acceptSuggestion(suggestion, suggestionIndex)">
          {{ suggestion.text }}
        </button>
      </div>
    </div>
    <!-- end AI Message Suggestions -->
  </div>
</template>

<style scoped>
.tagged-user {
  font-weight: bold;
  color: var(--link-primary);
  background-color: var(--background-brand-subtle);
  padding: 2px 5px;
  border-radius: 3px;
}

.message {
  overflow-wrap: anywhere;

  :deep(ul) {
    list-style: disc;
    padding-left: 1.75rem;
    margin-bottom: 1rem;
  }

  :deep(ol) {
    list-style: decimal;
    padding-left: 1.75rem;
    margin-bottom: 1rem;
  }
}

.message.tac-chat-intro-close {
  width: 100%;
}

/* Activity lines ("X joined/left the conversation.") span the full width so the centered text
   is centered against the whole chat window, not just the shrink-to-fit message container. */
.message.activity-line {
  width: 100%;
}

.message :deep(p) {
  margin-bottom: 0;
  line-height: 1.15rem;
}

.message :deep(p:has(+ p)) {
  margin-bottom: 1.1rem;
}

.tac-msg :deep(p:has(> em:first-child)) {
  padding-top: 8px;
  padding-left: 16px;
}

/* Ensure AI message container constrains table width for horizontal scrolling */
.spark-msg {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.spark-msg > div {
  min-width: 0;
  max-width: 100%;
}

/* Table responsive wrapper - ensure horizontal scrolling works */
.spark-msg :deep(.avery-table-responsive),
.tac-msg :deep(.avery-table-responsive) {
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Ensure TAC chat message container also constrains table width */
.tac-msg {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.tac-msg > div {
  min-width: 0;
  max-width: 100%;
}

/* Suggestion chips belong on the user's side of the thread. Every message block is capped
   at 86% width in the list (.message-list .message in app-fusion.scss) and the Rey bubble is
   left-aligned within that cap, so on their own the chips can only reach the right edge of
   that 86% block. Break the suggestions row out to the full tracking-row width — 100% / 0.86
   is the width of the enclosing row — so justify-end can pin each chip to the same right edge
   a user's own message aligns to. */
.avery-suggestions {
  width: calc(100% / 0.86);
}

.message :deep(.fact-cited) {
  position: relative;
  display: inline;
  cursor: help;
}

.message :deep(.fact-cite) {
  display: inline-flex;
  align-items: center;
  margin-left: 1px;
  vertical-align: super;
  font-size: 0.7em;
  cursor: help;
  position: relative;
  outline: none;
}

.message :deep(.fact-cite-tooltip) {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1a1a2e;
  color: #e0e0e0;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: normal;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  min-width: 120px;
  max-width: 300px;
  width: max-content;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.message :deep(.fact-cite:hover .fact-cite-tooltip),
.message :deep(.fact-cite:focus .fact-cite-tooltip),
.message :deep(.fact-cited:hover .fact-cite-tooltip),
.message :deep(.fact-cited:focus-within .fact-cite-tooltip) {
  opacity: 1;
  pointer-events: auto;
}

.message :deep(.fact-cite-code) {
  display: block;
  margin-top: 2px;
}
</style>

<script>
import MessengerAvatar from "@components/Messaging/MessengerAvatar.vue";
import ToolCitations from "@components/UI/ToolCitations.vue";
import MessageTimestamp from "@components/Messaging/MessageTimestamp.vue";
import ImageContainer from "@components/UI/ImageContainer.vue";
import FusionDropdown from "@components/UI/FusionDropdown.vue";
import PromoteAttachmentButton from "@components/Messaging/PromoteAttachmentButton.vue";
import ConfirmationCard from "@components/Messaging/ConfirmationCard.vue";
import ScheduledResultCard from "@components/Scheduled/ScheduledResultCard.vue";
import ScheduledActivityCard from "@components/Scheduled/ScheduledActivityCard.vue";
import ScheduledForkBanner from "@components/Scheduled/ScheduledForkBanner.vue";
import BriefFindingCard from "@components/Scheduled/BriefFindingCard.vue";
import SparkIcon from '@images/spark_icon.svg';
import TacChatLogo from '@images/tac_chat_logo.svg';
import { markRaw } from 'vue';
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import MarkdownIt from 'markdown-it';
import { readTable, buildTablesWorkbook } from '../../utils/tableExport';
import { sanitizeHtml, safeUrl } from '../../utils/sanitizeHtml';
import { Picker, EmojiIndex } from "emoji-mart-vue-fast";
import data from "emoji-mart-vue-fast/data/all.json";
const emojiIndex = new EmojiIndex(data);

/**
 * Tasks already reported as read this session. Reading a delivered result resets the no-engagement
 * auto-pause clock, so it has to be reported — but the message list re-renders constantly, and one POST
 * per render would be a write storm to record a single act of reading.
 */
const engagementReported = new Set();

/**
 * How much of a finding's headline the "Let's chat about this" starter question quotes.
 *
 * A headline is one sentence by instruction (BriefFindingParser asks for exactly that), so this guards
 * against a turn that ignored the FORMAT — one that emitted a paragraph, or the whole brief — and not
 * against a sentence that is merely long. Those are different sizes: a wordy but legitimate headline
 * runs to roughly 250-300 characters, a turn that lost the format runs to thousands. The threshold sits
 * between them so it never fires on real output.
 *
 * It used to be 180, which does not separate those two cases — it fired on ordinary headlines and cut
 * them mid-sentence. That cost more than the look of the composer: the quoted headline is the ONLY
 * thing that carries this card into the turn, because a brief's cards are written to the messenger
 * thread while Rey's history is read from the conversation container, and nothing bridges the two. An
 * ellipsis here silently truncates what Rey is answering about, not just what the person sees.
 *
 * The composer is not one line and no longer needs protecting as though it were: it wraps, it scrolls
 * (overflow-y: auto), and in MESSENGER mode it stands at min-h-24 before anything is typed.
 */
const BRIEF_CHAT_PROMPT_MAX = 600;

/**
 * A metric-rows metadata value — serialized JSON on the wire, or already an array — as an array; anything
 * malformed reads as no rows rather than breaking the message. (NPM-225 A7, scheduled watcher cards.)
 */
function parseMetricRows(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string' || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

export default {
  props: {
    message: {
      type: Object,
      required: true
    },
    currentUserGuid: {
      type: String,
      required: true
    },
    usersByGuid: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    threadIsSystemAi: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * True when this message is being rendered in the Scheduled tasks thread.
     *
     * Gates the result-card header. A delivered result carries its task id wherever it sits, and the
     * results delivered before results moved into this thread are still in people's main Rey threads —
     * so the metadata alone cannot decide whether to dress a message as a card. The thread can.
     */
    threadIsScheduled: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * True when this message is being rendered in a generated morning-brief thread.
     *
     * Separate from threadIsScheduled and gating a different card, for the same reason that prop exists:
     * a finding carries its own metadata wherever it sits — a forked one keeps it — so the metadata
     * alone cannot decide whether to dress a message as a finding. The thread can.
     */
    threadIsBrief: {
      type: Boolean,
      required: false,
      default: false
    },
    senderIndex: {
      type: Number,
      required: false,
      default: 0
    },
    senderName: {
      type: String,
      required: false,
      default: ''
    },
    senderIcon: {
      type: String,
      required: false,
      default: ''
    },
    isStreamingMessage: {
      type: Boolean,
      required: false,
      default: false
    },
    streamingDisplayBuffer: {
      type: Object,
      required: false,
      default: null
    },
    replyStreamingDisplayBuffer: {
      type: Object,
      required: false,
      default: null
    },
    messageReply: {
      type: Object,
      required: false,
      default: null
    },
    toolCitations: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    messageFeedbackStatus: {
      type: [Boolean,String],
      required: false,
      default: false
    },
    messageComment: {
      type: String,
      required: false,
      default: ''
    },
    replyFeedbackStatus: {
      type: [Boolean,String],
      required: false,
      default: false
    },
    replyPublished: {
      type: Boolean,
      required: false,
      default: false
    },
    dealerRoofs: {
      type: Object,
      required: false,
      default: null
    },
    canUseAvReyAssist: {
      type: Boolean,
      required: false,
      default: false
    },
    messageSendReady: {
      type: Boolean,
      required: false,
      default: false
    },
    messageCancelled: {
      type: Boolean,
      required: false,
      default: false
    },
    isLatestAiMessage: {
      type: Boolean,
      required: false,
      default: false
    },
    isVerifying: {
      type: Boolean,
      required: false,
      default: false
    },
  },
  created() {
    this.markdown = markRaw(new MarkdownIt({
      html: true,
      xhtmlOut: true,
      breaks: true,
      linkify: true,
      typographer: true
    }));
    
    // Make all links open in a new tab
    const defaultRender = this.markdown.renderer.rules.link_open || function(tokens, idx, options, env, self) {
      return self.renderToken(tokens, idx, options);
    };
    
    this.markdown.renderer.rules.link_open = function(tokens, idx, options, env, self) {
      tokens[idx].attrSet('target', '_blank');
      tokens[idx].attrSet('rel', 'noopener noreferrer');
      return defaultRender(tokens, idx, options, env, self);
    };
  },
  mounted() {
    this.recordScheduledEngagement();
    this.loadChartJs();
    if (this.$FUSarrayHasEntries(this.message.artifacts)) {
      const charts = this.message.artifacts.filter(a => a.type === 'Chart' || a.type === 0);
      if (charts.length > 0) {
        this.rendered_charts = charts;
        const self = this;
        const artifactsSnapshot = charts.map(a => ({ ...a }));
        this.$nextTick(() => {
          setTimeout(() => { self.initMessageChart({ artifacts: artifactsSnapshot }); }, 100);
        });
      }
    }

    // Start streaming if buffer exists and isn't already typing
    if (this.streamingDisplayBuffer && !this.is_typing && this.streamingDisplayBuffer.buffer)
      this.startStreamingTypingEffect();

    // Start streaming for reply if buffer exists
    if (this.replyStreamingDisplayBuffer && !this.is_typing_reply && this.replyStreamingDisplayBuffer.buffer) {
      console.log('[MessageItem] Reply streaming buffer found on mount, starting streaming');
      this.startReplyStreamingTypingEffect();
    }

  },
  beforeUnmount() {
    const self = this;
    // Clean up streaming intervals
    if (this.streaming_interval)
      clearInterval(self.streaming_interval);
    
    if (this.streaming_reply_interval)
      clearInterval(self.streaming_reply_interval);
    
    // Add cleanup for copy button event listeners
    const copyButtons = this.$el?.querySelectorAll('.copy-code-btn');
    copyButtons?.forEach(btn => {
      btn.removeEventListener('click', this.handleCopyCode);
    });
    
    // Clear the markdown instance
    this.markdown = null;
    
    // Clear caches
    this.rendered_markdown_cache = {};
    this.initialized_charts.clear();
    this.rendered_charts = [];
    this.chart_original_configs = {};

    if (this.comment_thank_you_timer) {
      clearTimeout(this.comment_thank_you_timer);
      this.comment_thank_you_timer = null;
    }
  },
  components: {
    'messenger-avatar': MessengerAvatar,
    'tool-citations': ToolCitations,
    'message-timestamp': MessageTimestamp,
    'image-container': ImageContainer,
    'fusion-dropdown': FusionDropdown,
    'promote-attachment-button': PromoteAttachmentButton,
    'confirmation-card': ConfirmationCard,
    'brief-finding-card': BriefFindingCard,
    'scheduled-result-card': ScheduledResultCard,
    'scheduled-activity-card': ScheduledActivityCard,
    'scheduled-fork-banner': ScheduledForkBanner,
  },
  data() {
    return {
      copy_success: false,
      markdown: null,
      rendered_markdown_cache: {},
      dealer_for_viewing_appraisal: {},
      attachment_errors: [],
      ai_message_class: 'bg-[radial-gradient(ellipse_228.63%_111.25%_at_22.50%_-32.35%,_rgba(145,_123,_255,_0.20)_22%,_rgba(107.50,_166.72,_255,_0.20)_100%)]',
      // The card footer's conversational button, shared by a scheduled result and a brief finding so the
      // two cannot drift apart. The fill is MIXED to an opaque colour rather than a see-through
      // bg-brand-subtle/30: the card behind it is itself a translucent lavender gradient (the class
      // above), and a see-through blue over that came out lavender too. Mixed against the field-focus
      // surface, so it still flips with the theme.
      card_chat_button_class: 'inline-flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap pl-2 pr-2.5 py-1 rounded-lg bg-[color:color-mix(in_srgb,var(--background-brand-subtle)_20%,var(--background-field-focus))] outline outline-1 outline-offset-[-1px] outline-brand-subtle text-text-link text-sm font-medium cursor-pointer hover:bg-[color:color-mix(in_srgb,var(--background-brand-subtle)_45%,var(--background-field-focus))]',
      // private_message_tooltip is a computed, not data: a data() literal is evaluated once and
      // would keep the old language after a switch.
      // Typewriter Levers:
      is_typing: false,
      is_typing_reply: false,
      pause_typing: null,
      pause_typing_reply: null,
      streaming_typing_speed: 30,          // Interval (ms) between ticks. Higher is slower.
      streaming_chars_per_tick: 3,       // Base characters per tick. Higher is faster.
      streaming_done_typing_speed: 90,   // Interval when websocket is finished.
      streaming_done_chars_per_tick: 6,  // Chars per tick when websocket is finished.
      streaming_interval: null,
      streaming_reply_interval: null,
      streaming_buffer_displayed: '',
      streaming_reply_buffer_displayed: '',
      initialized_charts: new Set(),
      // Pristine config JSON for each chart, captured at creation time *before*
      // theme overrides are applied. Used by retintInitializedCharts to rebuild
      // a chart from its original colors when switching back to light mode.
      chart_original_configs: {},
      spark_icon: SparkIcon,
      tac_chat_logo: TacChatLogo,
      suggestions_shown_at: null,
      suggestions_fired_ids: new Set(),
      // Feedback comment state (visible only when messageFeedbackStatus === 'ThumbsDown').
      // comment_box_open is hidden-by-default per UX: user must click "Add comment" to reveal the textarea.
      // comment_draft is the in-flight editor value; it diverges from messageComment prop while the user is typing
      // and gets re-synced on (a) prop change while box is closed, (b) successful save, or (c) vote change.
      comment_box_open: false,
      comment_draft: '',
      // Drives the small green "Thank you for your feedback." line. Only flipped on by submitComment()
      // when a save occurred during the current edit session. The 3s timer auto-fades it back out.
      comment_thank_you_visible: false,
      comment_thank_you_timer: null,
      // Tracks whether submitCommentIfChanged() actually emitted during the current open-editor session.
      // Reset to false in toggleCommentBox() (fresh session) and on vote-away. Consumed by submitComment()
      // to decide whether to surface the thank-you confirmation.
      comment_saved_since_open: false,
      // Chart artifacts that have been rendered — only grows, never cleared.
      // Prevents Vue from removing canvas elements when the message object is replaced.
      rendered_charts: [],
      // True while the all-tables workbook is being built, so a second click cannot start another.
      tables_download_processing: false,
    }
  },
  computed: {
    ...mapStores(useMainStore),
    /**
     * The scheduled task that produced this message, or null for any ordinary message.
     *
     * Delivery stamps it (ScheduledResultMetadata.TaskIdKey), so its presence is what distinguishes a
     * delivered recap or watcher alert from every other assistant message in the thread. Nothing here
     * guesses: no id, no offer to adjust.
     */
    scheduled_task_id() {
      const id = this.message?.metadata?.scheduledTaskId;
      return (typeof id === 'string' && id.trim()) ? id.trim() : null;
    },
    /**
     * The brief task that produced this finding card, or null.
     *
     * Its own key rather than scheduledTaskId (ScheduledResultMetadata.BriefTaskIdKey), because that one
     * would dress the card as a scheduled result and offer to adjust it. Read only for the engagement
     * report: opening the brief is what keeps it clear of the 30-day no-engagement pause. Gated on the
     * brief thread like brief_finding, so a forked copy of a finding reports nothing.
     */
    brief_task_id() {
      if (!this.threadIsBrief) return null;
      const id = this.message?.metadata?.briefFindingTaskId;
      return (typeof id === 'string' && id.trim()) ? id.trim() : null;
    },
    /**
     * The header-band props for a scheduled result, or null when this message should not be dressed
     * as one.
     *
     * Two conditions, both required: the message has to have been produced by a task (the task id),
     * and it has to be sitting in the Scheduled tasks thread. The second is what keeps results
     * delivered before the move — which are still in people's main Rey threads and are deliberately
     * not migrated — rendering as the ordinary messages they are there.
     *
     * The four fields are read leniently and default to empty. Metadata arrives through JSON from a
     * dictionary the server types as `object`, and a message written before these keys existed has
     * none of them; the card is built to render an absent field as absent rather than as a guess.
     */
    /**
     * The activity-card props for a scheduled run the gates held back, or null for every other
     * activity message.
     *
     * Keyed on the activity metadata, NOT on the thread: an activity message carrying
     * scheduledActivityKind can only have come from the held-notice path, so there is nothing to
     * disambiguate — unlike a delivered result, which carries a task id wherever it sits and needed the
     * thread to tell a card from an ordinary message.
     *
     * An ordinary join/left line carries none of these keys and falls through to the centered-line
     * treatment it has always had.
     */
    /**
     * The origin-banner props when this message is a forked thread's seed, or null otherwise.
     *
     * Keyed on `scheduledForkedFromTaskId`, which only the fork path stamps. NOT on
     * `scheduled_task_id` — the seed carries that too (it is a copy of a result, and reading it still
     * counts as engaging with the task), so the two have to be told apart by different keys. That is
     * why delivery stamps a separate one rather than reusing the result's.
     *
     * Thread-independent by design: a fork can be opened from anywhere, and the seed knows what it is.
     */
    scheduled_fork() {
      const metadata = this.message?.metadata || {};
      const text = value => (typeof value === 'string' ? value.trim() : '');

      if (!text(metadata.scheduledForkedFromTaskId)) return null;

      return {
        fromThreadId: text(metadata.scheduledForkedFromThreadId),
        runAt: text(metadata.scheduledForkedRunAt),
      };
    },
    scheduled_activity() {
      const metadata = this.message?.metadata || {};
      const text = value => (typeof value === 'string' ? value.trim() : '');

      const kind = text(metadata.scheduledActivityKind);
      if (!kind) return null;

      return {
        kind,
        deferredUntil: text(metadata.scheduledDeferredUntil),
      };
    },
    /**
     * True when this message is a scheduled result that asked a question and is still waiting.
     *
     * Read from the delivered outcome rather than tracked separately, so it cannot drift from what the
     * card is displaying. Independent of the thread flag on purpose: a question needs answering
     * wherever it is being read.
     */
    scheduled_awaiting() {
      const outcome = this.message?.metadata?.scheduledOutcome;
      return typeof outcome === 'string' && outcome.trim() === 'AwaitingUser';
    },
    /**
     * The finding card carried by this message, or null for everything else.
     *
     * Gated on the BRIEF thread and on the finding key together, the same pairing scheduled_result uses:
     * the key alone would dress a forked finding as a card in the thread it was forked into, where the
     * copy is a snapshot and the brief it came from is the live record.
     *
     * The metrics arrive as a JSON string in the metadata bag, so parsing can fail on a message written
     * by an older server. A failed parse costs the card its figures rather than the whole card — the
     * headline is the message body and renders regardless.
     */
    brief_finding() {
      if (!this.threadIsBrief) return null;

      const metadata = this.message?.metadata || {};
      const text = value => (typeof value === 'string' ? value.trim() : '');

      const findingKey = text(metadata.briefFindingKey);
      if (!findingKey) return null;

      let metrics = [];
      const raw = metadata.briefFindingMetrics;
      if (typeof raw === 'string' && raw.trim()) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) metrics = parsed;
        } catch (error) {
          metrics = [];
        }
      } else if (Array.isArray(raw)) {
        metrics = raw;
      }

      return {
        findingKey,
        domain: text(metadata.briefFindingDomain),
        status: text(metadata.briefFindingStatus),
        producedAt: text(metadata.scheduledRunAt) || text(metadata.briefFindingDate),
        dealerId: text(metadata.scheduledDealerId),
        metrics,
        definition: text(metadata.briefFindingDefinition),
        scope: text(metadata.briefFindingScope),
        scopeGroupId: text(metadata.briefFindingScopeGroupId),
      };
    },
    /**
     * Whether this finding is one there is anything to say about.
     *
     * Gathered findings only. A domain whose data could not be read has nothing to follow up ON — the
     * card states a fetch failure, not a fact about the store — and the same test excludes the card a
     * brief that gathered nothing posts, which is reported unavailable for exactly that reason.
     *
     * Named for the conversation rather than the fork it used to gate: the eligibility rule is the same
     * one the server still applies to a fork, but what it permits here is a question asked in the brief
     * thread, which never reaches that endpoint.
     */
    brief_finding_chattable() {
      return !!this.brief_finding && this.brief_finding.status !== 'Unavailable';
    },
    /**
     * The starter question "Let's chat about this" writes into the composer, or '' when there is
     * nothing to quote.
     *
     * Quotes the finding's own sentence rather than naming its domain. The body IS the headline —
     * delivery writes it as the message content and keeps every figure in metadata — so quoting it asks
     * about the exact thing the card states, and it stays right even where the card's heading falls
     * back to a humanised fact key.
     *
     * Whitespace is collapsed before the quote is built: the composer is a single-line contenteditable,
     * and a headline carrying a newline would seed a message whose second line is invisible until the
     * person scrolls the box.
     */
    /**
     * What the finding's brief ran on, handed up with the starter question so the composer's dealership
     * filter can be put on the same thing: the question is about the stores the card shows, and asked
     * under the page's own store it would reach only that one. `{ groupId, groupName }` for a group
     * brief, `{ dealerId }` for one store, or null when the card names neither (an older card).
     */
    brief_chat_scope() {
      const finding = this.brief_finding;
      if (!finding) return null;
      if (finding.scopeGroupId) return { groupId: finding.scopeGroupId, groupName: finding.scope || '' };
      if (finding.dealerId) return { dealerId: finding.dealerId };
      return null;
    },
    brief_chat_prompt() {
      const headline = (this.message_content || '').replace(/\s+/g, ' ').trim();
      if (!headline) return '';

      const finding = headline.length > BRIEF_CHAT_PROMPT_MAX
        ? `${headline.slice(0, BRIEF_CHAT_PROMPT_MAX).trimEnd()}…`
        : headline;

      return this.$t('messaging.briefFinding.chatPrompt', { finding });
    },
    scheduled_result() {
      if (!this.threadIsScheduled || !this.scheduled_task_id) return null;

      const metadata = this.message?.metadata || {};
      const text = value => (typeof value === 'string' ? value.trim() : '');

      return {
        taskName: text(metadata.scheduledTaskName),
        runAt: text(metadata.scheduledRunAt),
        outcome: text(metadata.scheduledOutcome),
        dealerId: text(metadata.scheduledDealerId),
        // NPM-225 A7: a fired watcher's card. Absent keys give empty values and the card shows none of it.
        headline: text(metadata.scheduledWatcherHeadline),
        metrics: parseMetricRows(metadata.scheduledWatcherMetrics),
        definition: text(metadata.scheduledWatcherDefinition),
        sources: text(metadata.scheduledWatcherSources),
      };
    },
    /**
     * The thread an attachment on this message would be promoted out of. Taken from the message rather
     * than from the open thread so a message rendered in any list promotes into the right place. Null
     * disables the offer entirely — the promotion is thread-scoped and has nothing to work from without it.
     */
    thread_id_for_promotion() {
      return this.message?.threadId || null;
    },

    /**
     * The confirmation card carried by this message, or null for every ordinary message.
     *
     * Rey can ask to do something that changes data — deleting a report, say — but the agent loop is
     * not allowed to carry it out: the call is intercepted before it reaches the MCP and parked
     * server-side, and this card is the handle on it. Clicking Yes is what performs it.
     *
     * Recognised by parsing the message body rather than by a flag on the message, because the body is
     * the only thing the messaging layer carries end to end — the same reason the file and appraisal
     * cards are recognised that way. A body that is not this card parses to null and the message
     * renders as markdown exactly as before.
     */
    confirmation_request() {
      const content = this.message_content;
      // Cheap reject first: every ordinary message would otherwise pay for a JSON.parse attempt.
      if (!content || content.indexOf('ConfirmationRequest') < 0) return null;

      try {
        const parsed = JSON.parse(content);
        const card = parsed && parsed.ConfirmationRequest;
        // An id is what makes the card answerable; without one there is nothing to render but a
        // dead-end, so fall through to the normal rendering instead.
        return card && card.Id ? card : null;
      } catch (e) {
        return null;
      }
    },

    /**
     * FUS-437: whether the card above may be answered. The server marks the message that carries a real
     * card with the decision id in its metadata; the body alone is text that any assistant-authored reply
     * can contain, and a look-alike carrying one of this user's real pending decision ids would otherwise
     * run that decision under different words. Unmarked cards (including ones sent before the marker
     * existed, whose decisions have long expired) render without buttons.
     */
    confirmation_is_live() {
      const card = this.confirmation_request;
      return !!card && this.message?.metadata?.confirmationDecisionId === card.Id;
    },

    // Active UI catalog locale. Read here so renderAveryMarkdown's cache key can include it — the
    // rendered HTML carries a localized citation aria-label, so a cached render would otherwise
    // keep the previous language after a switch. Read from the resolved i18n instance (like
    // Navbar.vue's active_locale) rather than mainStore.language so a switch between a base and a
    // regional catalog of the SAME primary (en → en-GB, fr-CA → fr-FR) also changes the key —
    // the store's primary subtag stays constant across those.
    ui_language() {
      return this.$i18n ? this.$i18n.locale : this.mainStore.language;
    },
    private_message_tooltip() {
      return this.$t('messaging.privateMessageTooltip');
    },
    ai_chat_thread_id() {
      return this.mainStore.ai_chat_thread_id;
    },
    rey_session_id() {
      return this.mainStore.rey_session_id;
    },
    current_theme() {
      // Exposed for the watcher below — when the active theme changes we
      // re-tint every Chart.js instance this component owns.
      return this.mainStore.theme;
    },
    component_key() {
      return this.message.id?`message_item_${this.message.id}`:('message_item_'+Date.now());
    },
    dealer_roofs_array() {
      if (!this.dealerRoofs) return [];
      return Object.entries(this.dealerRoofs).map(([guid, name]) => ({ value: guid, label: name }));
    },
    component_id() {
      return this.component_key+'_container';
    },
    emoji_index() {
      return emojiIndex || null;
    },
    message_content() {
      return (this.message.content || '').replace(/<viz-[a-zA-Z0-9]+>/g, '');
    },
    chart_artifacts() {
      if (!this.$FUSarrayHasEntries(this.message.artifacts)) return [];
      return this.message.artifacts.filter(a => a.type === 'Chart' || a.type === 0);
    },
    /**
     * How many tables this answer shows: markdown tables in the text plus chart artifacts that
     * initMessageChart renders as a table (tableData in 'table' or 'mixed' mode). Counted from the
     * data rather than the DOM because chart tables are inserted on a timer after mount.
     */
    exportable_table_count() {
      // Same source as buildMarkdownCopyText: message.content is blanked for streamed messages.
      const raw = (this.streamingDisplayBuffer?.buffer || this.message.content || '').replace(/<viz-[a-zA-Z0-9]+>/g, '');
      const markdownTables = raw.includes('|') && this.markdown
        ? this.markdown.parse(raw, {}).filter(token => token.type === 'table_open').length
        : 0;
      const chartTables = this.rendered_charts.filter(artifact => {
        if (!artifact.configurationJson) return false;
        try {
          const config = JSON.parse(artifact.configurationJson);
          return !!config.tableData && (config.mode === 'table' || config.mode === 'mixed');
        } catch (e) {
          return false;
        }
      }).length;
      return markdownTables + chartTables;
    },
    message_item_type() {
      let senderId = this.message?.senderId || null;
      // Activity lines ("X joined/left the conversation.") are marked by type, not sender, so they
      // render as a subtle centered line regardless of who they are about.
      if(this.message?.type === 'activity')
        return 'ACTIVITY_MESSAGE';
      if(senderId === 'system_TACCHAT') {
        if(this.tac_chat_open_message)
          return 'TACCHAT_INTRO';
        if(this.tac_chat_close_message)
          return 'TACCHAT_CLOSE';
        
        return 'TACCHAT_MESSAGE';
      }
      if(senderId?.indexOf(this.$FUSsystemUserPrefix)>=0)
        return 'AI_MESSAGE';
      if(senderId === this.currentUserGuid)
        return 'USER_MESSAGE';
      
      return 'STANDARD_MESSAGE';
    },
    is_current_user_message() {
      return this.message_item_type === 'USER_MESSAGE';
    },
    is_tacchat_message() {
      return this.message_item_type === 'TACCHAT_MESSAGE';
    },
    is_tacchat_intro() {
      return this.message_item_type === 'TACCHAT_INTRO';
    },
    is_tacchat_close() {
      return this.message_item_type === 'TACCHAT_CLOSE';
    },
    is_ai_message() {
      return this.message_item_type === 'AI_MESSAGE';
    },
    // True only when the avatar is showing the Rey spark, which is a currentColor SVG served through
    // <img> and therefore renders black. Keyed off the resolved src rather than is_ai_message so the
    // multi-color TAC logo (also returned by getSenderImage) is never tinted.
    sender_image_is_glyph() {
      return this.getSenderImage(this.message.senderId) === this.spark_icon;
    },
    is_standard_message() {
      return this.message_item_type === 'STANDARD_MESSAGE';
    },
    is_activity_message() {
      return this.message_item_type === 'ACTIVITY_MESSAGE';
    },
    image_attachment_urls() {
      // Check if message content is a JSON image attachment
      // Format: {"attachment":{"type":"image","payload":{"urls":[...]}}}
      const content = this.message?.content;
      if (!content || typeof content !== 'string') return null;
      
      // Quick check before parsing - must start with { and contain "attachment"
      if (!content.startsWith('{') || !content.includes('"attachment"')) return null;
      
      try {
        const parsed = JSON.parse(content);
        if (parsed?.attachment?.type === 'image' && 
            parsed?.attachment?.payload?.urls && 
            Array.isArray(parsed.attachment.payload.urls) &&
            parsed.attachment.payload.urls.length > 0) {
          return parsed.attachment.payload.urls;
        }
      } catch (e) {
        // Not valid JSON, return null
      }
      return null;
    },
    tac_chat_data() {
      return this.message?.tacChatData || null;
    },
    tac_chat_open_message() {
      return Boolean(this.tac_chat_data?.isChatOpenMessage);
    },
    tac_chat_close_message() {
      return Boolean(this.tac_chat_data?.isChatCloseMessage);
    },
    tac_chat_agent_name() {
      if(this.tac_chat_data && this.tac_chat_data.agentFirstName && this.tac_chat_data.agentLastName)
        return `${this.tac_chat_data.agentFirstName} ${this.tac_chat_data.agentLastName}`;
      
      return null;
    },
    tac_chat_agent_title() {
      if(this.tac_chat_data && this.tac_chat_data.agentFirstName && this.tac_chat_data.agentLastName)
        return this.tac_chat_data.agentTitle;
      
      return null;
    },
  },
  methods: {
    safeUrl,
    /**
     * Reports that this delivered scheduled result was read (§5.3). Best-effort and fire-and-forget: it
     * resets an auto-pause clock, and failing to record a visit must never disturb reading the thread.
     */
    recordScheduledEngagement() {
      const taskId = this.scheduled_task_id || this.brief_task_id;
      if (!taskId || engagementReported.has(taskId)) return;
      engagementReported.add(taskId);
      this.mainStore.RecordScheduledTaskEngagement(taskId);
    },
    copyMessageToClipboard(source) {
      if (source === 'reply') {
        const raw = this.messageReply?.content || '';
        this.writeToClipboard(raw.replace(/<[^>]*>/g, ''));
      } else {
        this.writeToClipboard(this.buildMarkdownCopyText());
      }
      this.copy_success = true;
      setTimeout(() => { this.copy_success = false; }, 250);
    },
    buildMarkdownCopyText() {
      // streamingDisplayBuffer.buffer holds the raw markdown; message.content is blanked for
      // streaming messages by initMessageStreamingBuffer (MessengerWindow line ~1992).
      const raw = this.streamingDisplayBuffer?.buffer || this.message.content || this.streaming_buffer_displayed || '';
      if (!raw) return '';
      const withTables = raw.replace(/<viz-([a-zA-Z0-9]+)>/g, (match, dynamicId) => {
        const chartId = `viz-${dynamicId}`;
        const artifact = this.message?.artifacts?.find(a => a.id === chartId);
        if (!artifact?.configurationJson) return '';
        try {
          const config = JSON.parse(artifact.configurationJson);
          if (config.tableData) return this.tableDataToMarkdown(config.tableData);
        } catch (e) { /* ignore malformed artifact config */ }
        return '';
      });
      return withTables.replace(/<[^>]*>/g, '').trim();
    },
    // Reads the tables off the page rather than from the message data, like the per-table download button,
    // so the workbook matches what is on screen. Scoped to this message's element: chart ids (viz-1)
    // repeat across messages, and document order here is the order the person sees them in.
    downloadAllTables() {
      if (this.tables_download_processing) return;
      const tables = Array.from(this.$el?.querySelectorAll('table.avery-table') || [])
        .map(tableElement => readTable(tableElement))
        .filter(table => table.rows.length > 0);
      if (tables.length === 0) return;

      this.tables_download_processing = true;
      buildTablesWorkbook(tables).then(blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `rey-tables-${this.$FUSdateFormat('YYYY-MM-DD', new Date())}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }).catch(error => {
        console.error('[MessageItem] Unable to build the tables workbook', error);
      }).finally(() => {
        this.tables_download_processing = false;
      });
    },
    tableDataToMarkdown(tableData) {
      if (!tableData?.columns || !tableData?.rows) return '';
      const escapeCell = val => String(val ?? '').replace(/\|/g, '\\|').replace(/\r?\n/g, ' ');
      const cols = tableData.columns.map(escapeCell);
      const header = '| ' + cols.join(' | ') + ' |';
      const separator = '| ' + cols.map(() => '---').join(' | ') + ' |';
      const body = tableData.rows.map(row =>
        '| ' + row.map(escapeCell).join(' | ') + ' |'
      ).join('\n');
      return '\n' + header + '\n' + separator + '\n' + body + '\n';
    },
    writeToClipboard(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    },
    loadChartJs() {
      if (window.Chart) {
        // Chart.js already loaded
        return;
      }
      
      const script = document.createElement('script');
      // [FUS-387] Pinned: an unpinned CDN script runs whatever is published next. 4.5.1 is the version
      // the unpinned URL served.
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.5.1';
      script.async = true;
      
      script.onload = () => {
        //chart loaded
      };
      
      script.onerror = (error) => {
        console.error('Failed to load Chart.js', error);
      };
      
      document.head.appendChild(script);
    },
    fallbackRender(content) {
      return content
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`([^`]*)`/g, '<code>$1</code>')
        .replace(/^### (.*$)/gm, '<h3>$1</h3>')
        .replace(/^## (.*$)/gm, '<h2>$1</h2>')
        .replace(/^# (.*$)/gm, '<h1>$1</h1>');
      // .replace(/\r\n/g, '<br>')
      // .replace(/\n/g, '<br>');
    },
    removeValidJSON(text) {
      // More comprehensive regex for JSON objects and arrays
      const jsonPattern = /(\{(?:[^{}]|{(?:[^{}]|{[^{}]*})*})*\}|\[(?:[^\[\]]|\[(?:[^\[\]]|\[[^\[\]]*\])*\])*\])/g;
      return text.replace(jsonPattern, (match) => {
        try {
          // Normalize curly quotes to straight quotes
          const normalizedMatch = match
            .replace(/[“”]/g, '"'); // Replace curly single quotes if needed
          
          JSON.parse(normalizedMatch.trim());
          return ''; // Remove valid JSON
        } catch (e) {
          return match; // Keep invalid JSON-like text
        }
      }).replace(/\n\s*\n\s*\n/g, '\n\n').trim();
    },
    renderAveryMarkdown(content, messageId=null) {
      // [FUS-387 / Security Backlog H1] Everything this returns goes to v-html, and markdown-it
      // runs with html: true, so every return path is sanitized — the fallback included.
      if (!this.markdown) {
        return sanitizeHtml(this.fallbackRender(content));
      }
      
      // Strip <viz-N> tags — charts are rendered from artifacts array in the template
      content = content.replace(/<viz-[a-zA-Z0-9]+>/g, '');
      
      // Check cache first — include a version tag so render code changes invalidate the cache
      const cacheKey = messageId + (this.message.artifacts?.length || 0) + '_v5_' + this.ui_language;
      if (messageId && this.rendered_markdown_cache && this.rendered_markdown_cache[cacheKey]) {
        const cached = this.rendered_markdown_cache[cacheKey];
        if (cached.content === content) {
          return cached.html;
        }
      }
      
      try {
        let rendered = this.markdown.render(content);

        // Convert {{value~source}} citation syntax into styled inline citations.
        // Everything injected is HTML-escaped — evidence text can contain quotes
        // and angle brackets that would otherwise shred the markup. A "— Based on:"
        // suffix (Calculator provenance) renders as a second, dimmer tooltip line.
        const escapeHtml = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        rendered = rendered.replace(/\{\{([^~}]+)~([^}]+)\}\}/g, (match, value, source) => {
          const sepIdx = source.indexOf('— Based on:');
          let tooltipHtml;
          if (sepIdx >= 0) {
            const label = escapeHtml(source.substring(0, sepIdx).trim());
            const basedOn = escapeHtml(source.substring(sepIdx + 1).trim());
            tooltipHtml = `${label}<span class="fact-cite-code">${basedOn}</span>`;
          } else {
            tooltipHtml = escapeHtml(source);
          }
          return `<span class="fact-cited">${escapeHtml(value)}<sup class="fact-cite" tabindex="0" role="button" aria-label="${escapeHtml(this.$t('messaging.citationSource', { source }))}"><span class="fact-cite-tooltip">${tooltipHtml}</span></sup></span>`;
        });

        // Fix Markdown Flickering: Account for typewriter progress
        if (rendered.includes('|')) {
          const isStreamingBuffer = this.streamingDisplayBuffer && 
                                   (this.streaming_buffer_displayed.length < this.streamingDisplayBuffer.buffer.length || !this.streamingDisplayBuffer.isComplete);
          const isStreamingReply = this.replyStreamingDisplayBuffer && 
                                  (this.streaming_reply_buffer_displayed.length < this.replyStreamingDisplayBuffer.buffer.length || !this.replyStreamingDisplayBuffer.isComplete);
          
          if (isStreamingBuffer || isStreamingReply) {
            rendered = rendered.replace(/:?-{1,}:?/g, (match) => ' '.repeat(match.length));
            rendered = rendered.replace(/\|/g, ' ');
          }
        }

        // Fix Chart Replacement: placeholder divs are no longer injected (processStreamPlaceholder
        // is a no-op). Charts render from the v-for template on the artifacts array.
        // This block is intentionally empty — kept as a marker for where the old logic was.

        if (rendered.includes('<table') && !rendered.includes('table-responsive')) {
          // The download bar sits OUTSIDE .avery-table-responsive on purpose: that wrapper is the
          // horizontal scroll container and carries a right-edge mask-image gradient, so a button
          // placed inside it would scroll away with the table and get clipped by the fade.
          // .answer-table-block then shrink-wraps the pair, so the right-aligned button lands on
          // the TABLE's right edge rather than the message column's — without it a narrow table
          // (a 2-3 column summary, or the single-record vertical pivot) leaves the icon floating
          // far off to the right, detached from what it downloads.
          let tableCounter = 0;
          rendered = rendered.replace(
            /<table/g,
            () => {
              const tableId = `table-block-${messageId || Date.now()}-${tableCounter++}`;
              return `<div class="answer-table-block">` +
                     `<p class="table-block-header mb-0 py-1 px-2 text-right"><span class="cursor-pointer download-table-btn" data-table-id="${tableId}" tabindex="0" role="button" aria-label="${escapeHtml(this.$t('messaging.downloadTable'))}" title="${escapeHtml(this.$t('messaging.downloadTable'))}"><i class="ri-download-2-line"></i></span></p>` +
                     `<div class="avery-table-responsive"><table id="${tableId}" class="avery-table"`;
            }
          ).replace(
            /<\/table>/g,
            '</table></div></div>'
          );
        }
        
        if (rendered.includes('<pre><code')) {
          let codeBlockCounter = 0;
          rendered = rendered.replace(
            /<pre><code/g,
            () => {
              const blockId = `code-block-${messageId || Date.now()}-${codeBlockCounter++}`;
              return `<p class="code-block-header mb-0 py-1 px-2 text-right"><span class="cursor-pointer copy-code-btn" data-code-block-id="${blockId}"><i class="ri-file-copy-line"></i> Copy</span></p><pre class="avery-pre-responsive"><code id="${blockId}"`;
            }
          );
        }
        
        let result = rendered;
        if(rendered.indexOf('{') >= 0)
          result = this.removeValidJSON(rendered);
        
        // Last step before caching, so the cache only ever holds sanitized html.
        result = sanitizeHtml(result);

        // Update cache
        if (messageId) {
          if(!this.rendered_markdown_cache) this.rendered_markdown_cache = {};
          this.rendered_markdown_cache[cacheKey] = {
            content: content,
            html: result
          };
        }
        
        return result;
      }
      catch (error) {
        // If markdown is incomplete during streaming, use fallback
        return sanitizeHtml(this.fallbackRender(content));
      }
    },
    isAppraisalMessage(obj) {
      const objKeys = Object.keys(obj);
      const appraisalMessageKeys = ['year', 'make', 'model', 'trim', 'mileage', 'exteriorColor', 'dealerName', 'dealerGuid', 'appraisalGuid', 'thumbnail', 'vin', 'messageType'];
      return objKeys.every(key => appraisalMessageKeys.includes(key)) && appraisalMessageKeys.every(key => objKeys.includes(key));
    },
    openAppraisal(appraisal, message_id = null) {
      const baseAppraisalUrl = `/app/${message_id ? this.dealer_for_viewing_appraisal[message_id] : appraisal.dealerGuid}/appraisals/appraise_vehicle`;
      
      const queryParams = appraisal.vin ? `vin_number=${appraisal.vin}&set_mileage=${appraisal.mileage}` : `appraisal_guid=${appraisal.appraisalGuid}`;
      
      const url = `${baseAppraisalUrl}?${queryParams}`;

      window.open(url);
    },
    tryParseJSON(jsonString) {
      try {
        const obj = JSON.parse(jsonString);
        if (obj && typeof obj === "object") {
          // Check for appraisal message
          if (this.isAppraisalMessage(obj)) {
            return { parsed: obj, type: 'appraisal' };
          }
          // Check for file attachment
          if (obj.Filename) {
            return { parsed: obj, type: 'file' };
          }
          // Check for image attachment: {attachment:{type:"image",payload:{urls:[...]}}}
          if (obj.attachment && obj.attachment.type === 'image' && 
              obj.attachment.payload && Array.isArray(obj.attachment.payload.urls)) {
            return { parsed: { urls: obj.attachment.payload.urls }, type: 'image' };
          }
        }
      } catch (e) {
      }
      
      return false;
    },
    splitMessageByPatterns(text) {
      const self = this;
      // Define the combined regex pattern for @username, :emoji:, _br_, and nested JSON
      // The JSON pattern matches balanced braces up to 3 levels deep
      const regex = /(@[\w-]+)|(_br_)|(:[\w-+]+(?:::[\w-]+)*:)|(\{(?:[^{}]|\{(?:[^{}]|\{[^{}]*\})*\})*\})/g;
      // Split the text by the regex
      const parts = text.split(regex).filter(Boolean);
      
      // Map the parts to an array of objects containing text and type
      return parts.map(part => {
        if (/^@[\w-]+$/.test(part)) {
          return { text: part, type: "tag" };
        } else if (/^_br_$/.test(part)) {
          return { text: part, type: "br" };
        } else if (/^:[\S]+:$/.test(part)) {
          return { text: part, type: "emoji" };
        } else if (/^\{/.test(part) && /\}$/.test(part)) {
          const parsedResult = self.tryParseJSON(part);
          if (parsedResult !== false) {
            // Return the appropriate type based on what tryParseJSON detected
            return { object: parsedResult.parsed, type: parsedResult.type === 'appraisal' ? 'json' : parsedResult.type };
          }
          return { text: part, type: "text" };
        } else {
          return { text: part, type: "text" };
        }
      });
    },
    formattedMessage(message) {
      const self = this;
      return message.replace(/@([\w-]+)/g, (match, p1) => {
        const user = self.usersByGuid && self.usersByGuid[p1]?self.usersByGuid[p1]:null;
        return user ? `@${user}` : match;
      });
    },
    replaceVizChart(content, artifacts) {
      const artifactList = artifacts || this.message.artifacts;
      if(content && content.indexOf('<viz-')>=0 && artifactList) {
        return content.replace(/<viz-([a-zA-Z0-9]+)>/g, (match, dynamicId) => {
          const chartId = `viz-${dynamicId}`;
          const artifact = artifactList.find(a => a.id === chartId);
          if (!artifact)
            return '';
          if (artifact.configurationJson) {
            try {
              const config = JSON.parse(artifact.configurationJson);
              if (config.mode === 'table') {
                return `<div id="chart-container-${chartId}" style="display: none;"></div>\n\n`;
              } else if (config.mode === 'mixed') {
                return `<div id="chart-container-${chartId}" style="display: none; width: 100%; height: 400px; position: relative;"><canvas id="${chartId}"></canvas></div>\n\n`;
              }
            } catch (e) {
              console.error("Fusion: Failed to parse artifact config for mode check", e);
            }
          }
          
          return `<div id="chart-container-${chartId}" style="display: none; width: 100%; height: 400px; position: relative;"><canvas id="${chartId}"></canvas></div>\n\n`;
        });
      }
      return content;
    },
    findEmoji(emojiTxt) {
      return this.emoji_index.findEmoji(emojiTxt) || null;
    },
    publishMessage(threadId, messageId) {
      this.$emit('publishMessage', threadId, messageId);
    },
    fireSuggestionShownEvents(suggestions) {
      let firedAny = false;
      suggestions.forEach((suggestion, index) => {
        const suggestion_id = `${this.message.id}_${index}`;
        if (this.suggestions_fired_ids.has(suggestion_id)) return;
        this.suggestions_fired_ids.add(suggestion_id);
        if (!firedAny) { this.suggestions_shown_at = Date.now(); firedAny = true; }
        window.Analytics?.enqueue('suggestion_shown', {
          session_id:       this.rey_session_id,
          suggestion_id,
          suggestion_type:  'ai_followup',
          message_id:       this.message.id,
          conversation_id:  this.ai_chat_thread_id,
        });
      });
    },
    acceptSuggestion(suggestion, index) {
      const suggestion_id = `${this.message.id}_${index}`;
      const time_to_accept_ms = this.suggestions_shown_at !== null ? Date.now() - this.suggestions_shown_at : null;
      window.Analytics?.enqueue('suggestion_accepted', {
        session_id:         this.rey_session_id,
        suggestion_id,
        suggestion_type:    'ai_followup',
        message_id:         this.message.id,
        conversation_id:    this.ai_chat_thread_id,
        time_to_accept_ms,
      });
      // Frustration follow-up: when the user clicks the TAC escalation suggestion, also fire
      // a discrete event so escalation acceptance can be reported without parsing suggestion text downstream.
      if (suggestion?.text === 'Chat with Support Agents') {
        window.Analytics?.enqueue('escalation_offer_accepted', {
          session_id:      this.rey_session_id,
          message_id:      this.message.id,
          conversation_id: this.ai_chat_thread_id,
          suggestion_text: suggestion.text,
        });
      }
      this.sendSystemMessage(suggestion.text);
    },
    sendSystemMessage(message='', systemId='system_REY') {
      let taggedMessage = message;
      if(!this.threadIsSystemAi)
        taggedMessage = `@${systemId} ${taggedMessage}`;
      this.$emit('sendMessage', taggedMessage);
    },
    submitFeedback(messageId, feedbackType, comment='') {
      if (this.messageFeedbackStatus === true) return;  // pending — block while awaiting response
      const alreadyRated = feedbackType === 0 ? 'ThumbsUp' : 'ThumbsDown';
      if (this.messageFeedbackStatus === alreadyRated) return;  // same vote re-click — no-op
      window.Analytics?.enqueue('response_feedback_submitted', {
        session_id:      this.rey_session_id,
        message_id:      messageId,
        conversation_id: this.ai_chat_thread_id,
        feedback:        feedbackType === 0 ? 'positive' : 'negative',
      });
      const submitFeedbackDTO = {
        conversationId: null,
        messageId: messageId,
        feedbackType: feedbackType, // 0 for ThumbsUp, 1 for ThumbsDown
        comment: comment
      };
      this.$emit('submitFeedback', submitFeedbackDTO);
    },
    toggleCommentBox() {
      // Open the textarea and focus it on the next tick so the user can start typing immediately.
      // Pre-fill from the prop so editing an existing comment is seamless. Reset the session-save tracker
      // so any prior thank-you state doesn't carry into the new edit session.
      this.comment_draft = this.messageComment || '';
      this.comment_box_open = true;
      this.comment_saved_since_open = false;
      this.$nextTick(() => {
        if (this.$refs.comment_input) this.$refs.comment_input.focus();
      });
    },
    submitComment() {
      // Explicit submit — invoked by the "Submit comment" button and by Enter. Blur no longer auto-saves,
      // so flush any change here first. If anything was actually saved during this edit session, surface the
      // thank-you confirmation, then collapse the editor.
      this.submitCommentIfChanged();
      if (this.comment_saved_since_open) {
        this.showCommentThankYou();
        this.comment_saved_since_open = false;
      }
      this.comment_box_open = false;
    },
    submitCommentIfChanged() {
      // Idempotency: only emit when the draft actually differs from the persisted prop value.
      // Without this guard, a Tab-through or click-away on an unchanged box would trigger a needless re-submit
      // (and a needless WebPubSub broadcast).
      const draft = (this.comment_draft || '').trim();
      const current = (this.messageComment || '').trim();
      if (draft === current) {
        // Still close the box on blur even when unchanged — keeps the UX consistent (focus loss = done).
        if (!draft) this.comment_box_open = false;
        return;
      }
      this.$emit('submitComment', { messageId: this.message.id, comment: draft });
      // Flag the session as having saved something so submitComment() can show the thank-you.
      this.comment_saved_since_open = true;
      // Keep the box open and don't reset the draft — the parent will round-trip via SubmitFeedback and the
      // updated messageComment prop will arrive shortly after. If the user keeps typing during that window,
      // the watcher below skips the prop-sync to avoid clobbering active input.
    },
    showCommentThankYou() {
      this.comment_thank_you_visible = true;
      if (this.comment_thank_you_timer) clearTimeout(this.comment_thank_you_timer);
      this.comment_thank_you_timer = setTimeout(() => {
        this.comment_thank_you_visible = false;
        this.comment_thank_you_timer = null;
      }, 3000);
    },
    renderPublishedMessage() {
      if(!this.messageReply)
        return '';
      
      // Check if reply is being streamed
      let replyContent = '';
      if (this.streaming_reply_buffer_displayed) {
        replyContent = this.streaming_reply_buffer_displayed;
      } else {
        replyContent = this.messageReply.content || '';
      }
      
      let replyId = this.messageReply.id;
      let replyMessageType = this.messageReply.type;
      
      // For replies, inject chart canvas divs from <viz-N> tags (no v-for template).
      // renderAveryMarkdown will strip any remaining <viz-N> tags and pass through HTML divs.
      let contentWithCharts = this.replaceVizChart(replyContent, this.messageReply.artifacts);
      let updatedReplyContent = this.renderAveryMarkdown(contentWithCharts, replyId);
      
      // Initialize charts if needed (only for regular messages, not streamStart)
      if (replyMessageType === 'message' && this.$FUSarrayHasEntries(this.messageReply.artifacts) &&
          this.messageReply.artifacts.some(a => a.type === 'Chart' || a.type === 0)) {
        const self = this;
        setTimeout(() => {
          self.initMessageChart(self.messageReply);
        }, 150);
      }
      return updatedReplyContent;
    },
    initMessageChart(data) {
      const self = this;
        if(this.$FUSarrayHasEntries(data.artifacts)) {
        data.artifacts.forEach(artifact => {
          if(artifact.type === 'Chart' || artifact.type === 0) {
            let chartId = artifact.id;
            if (this.initialized_charts.has(chartId)) {
              return;
            }
            
            let chartConfig = artifact.configurationJson?JSON.parse(artifact.configurationJson):null;
            
            // Use setTimeout instead of $nextTick — the component may be re-rendering
            // due to messages array updates, which can cause $nextTick to not fire.
            setTimeout(() => {
                // Canvas is rendered from the v-for template on rendered_charts,
                // which persists across message object replacements.
                // Scope lookup to this component's root element — multiple messages
                // may share the same chart ID (e.g. viz-1) across conversation history.
                var ctxElement = self.$el?.querySelector(`#${CSS.escape(chartId)}`);
                var ctxElementParent = ctxElement?.parentElement;
                const originalWidth = ctxElement?.width;
                // Get container width from the component's root element
                const containerWidth = self.$el?.offsetWidth || 800;
                const availableWidth = containerWidth - 16;
                
                if(ctxElementParent && ctxElementParent.style.display === 'block') {
                  return;
                }
                
                // Check if canvas exceeds available width
                if (ctxElement && ctxElementParent && originalWidth > availableWidth) {
                  ctxElement.removeAttribute('width');
                  ctxElement.removeAttribute('height');
                  ctxElement.style.width = '100%';
                  ctxElementParent.style.width = '100%';
                }
                
                var ctx = ctxElement?ctxElement.getContext('2d'):null;
                if(ctx && chartConfig && chartConfig.type) {
                  const hasUsefulChartData = chartConfig.type!=='table' && self.hasUsefulChartData(chartConfig);
                    if(hasUsefulChartData) {
                      if(self.$FUSarrayHasEntries(chartConfig.data.datasets)) {
                        for(let i = 0; i < chartConfig.data.datasets.length; i++) {
                          if(chartConfig.data.datasets[i] && i < self.$FUSfusionChartColorsHex.length) {
                            const dataset = chartConfig.data.datasets[i];
                            const color = self.$FUSfusionChartColorsHex[i];
                            
                            // Handle different chart types
                            if (chartConfig.type === 'pie' || chartConfig.type === 'doughnut')
                              dataset.backgroundColor = self.$FUSfusionChartColorsHex.slice(0, chartConfig.data.labels?.length || 1);
                            else {
                              dataset.backgroundColor = color;
                              // For line charts, also set border color
                              if (chartConfig.type === 'line')
                                dataset.borderColor = color;
                            }
                          }
                        }
                      }
                      let parentElements = self.$el?.querySelectorAll(`#chart-container-${chartId}`) || [];
                      for(let i = 0; i < parentElements.length; i++) {
                        if(i===0)
                          parentElements[i].style.display = 'block';
                        if(i > 0)
                          parentElements[i].remove();
                      }
                      
                      setTimeout(() => {
                        // [NPM-73] Normalise sizing for every producer, including configs the model
                        // generates from the prompt template. This must run BEFORE the snapshot below:
                        // retintInitializedCharts rebuilds each chart from that pristine copy on every
                        // theme change, so a normalisation placed after it survives the first render
                        // and is lost on the first light/dark toggle. Harmless for pie and doughnut —
                        // Chart.js derives their radius from the smaller dimension, so they gain
                        // whitespace rather than distortion.
                        chartConfig.options = chartConfig.options || {};
                        chartConfig.options.responsive = true;
                        chartConfig.options.maintainAspectRatio = false;

                        // Snapshot the pristine config *before* we mutate it with
                        // theme overrides — retintInitializedCharts uses this to
                        // rebuild from originals when the user switches to light.
                        self.chart_original_configs[chartId] = JSON.stringify(chartConfig);
                        self.applyChartTextThemeColor(chartConfig);
                        try {
                          new Chart(ctx, chartConfig);
                          this.initialized_charts.add(chartId);
                        } catch(chartErr) {
                          console.error(`Artifact chart unable to initialize. ID: ${chartId}`, chartErr);
                        }
                      }, 250);
                    }
                  }
                  
                  // [FIX] Table rendering moved OUTSIDE the ctx check so it works without a canvas
                  // Handle table rendering for 'table' or 'mixed' modes
                  if (chartConfig && chartConfig.tableData && (chartConfig.mode === 'table' || chartConfig.mode === 'mixed')) {
                    const tableContainer = self.$el?.querySelector(`#chart-container-${chartId}`);
                    // [FIX] Check if table already exists to prevent duplicates
                    const existingTable = self.$el?.querySelector(`#table-${chartId}`);
                    if (tableContainer && !existingTable) {
                      const tableHtml = self.generateTableHtml(chartConfig.tableData, chartId);
                      
                      // Create table element
                      const tableDiv = document.createElement('div');
                      tableDiv.className = 'avery-table-responsive mt-3';
                      tableDiv.innerHTML = tableHtml;
                      
                      // For mixed mode, insert after chart; for table-only, replace chart
                      if (chartConfig.mode === 'mixed') {
                        tableContainer.after(tableDiv);
                      } else {
                        // Table-only: hide chart container and insert table
                        tableContainer.style.display = 'none';
                        tableContainer.after(tableDiv);
                      }
                    } else if (existingTable) {
                      // Table already exists, skipping duplicate
                    }
                  }
              }, 100);
          }
        });
      }
    },
    applyChartTextThemeColor(chartConfig) {
      // Dark mode: override Chart.js text colors with a soft light-gray so
      // chart labels stay legible against the dark background without being
      // eye-stabbing pure white. Also bumps font weight for readability.
      // Light mode: no-op — let Chart.js use the original colors from the
      // backend's config (or its built-in '#666' default). We also reset
      // Chart.defaults.color so any subsequent chart picks up the right value.
      if (!chartConfig) return;
      const isDark = this._isDarkTheme();
      const textColor = this._resolveChartTextColor(isDark);
      const labelFontWeight = 600;

      this._pushChartGlobalDefaults(isDark, textColor);

      // Only mutate per-chart options when we actually need to override —
      // skipping in light mode preserves whatever the backend's config said.
      if (!isDark) return;

      chartConfig.options = chartConfig.options || {};
      this._themeChartOptions(chartConfig.options, textColor, labelFontWeight);
    },
    _pushChartGlobalDefaults(isDark, textColor) {
      // Push theme-aware colors into Chart.js's global defaults. Charts whose
      // config omits options.scales (most of ours) get scales auto-created at
      // render time from Chart.defaults.scale, so this is the *only* path that
      // affects them. Covers text (ticks/labels) AND gridlines + axis borders.
      if (!window.Chart || !window.Chart.defaults) return;

      window.Chart.defaults.color = textColor;

      const scaleDefaults = window.Chart.defaults.scale;
      if (!scaleDefaults) return;

      if (scaleDefaults.ticks) scaleDefaults.ticks.color = textColor;

      // Chart.js's built-in gridline default is 'rgba(0, 0, 0, 0.1)' — restore
      // that for light mode so we don't leave a dark-mode override behind.
      const gridDefault = isDark
        ? this._resolveChartGridColor()
        : 'rgba(0, 0, 0, 0.1)';

      if (scaleDefaults.grid) {
        scaleDefaults.grid.color = gridDefault;
        // v3 keeps the axis-border color on grid.borderColor.
        scaleDefaults.grid.borderColor = gridDefault;
      }
      // v4 moved the axis-border to its own block.
      if (scaleDefaults.border) {
        scaleDefaults.border.color = gridDefault;
      }
    },
    _isDarkTheme() {
      return document.documentElement.getAttribute('data-theme') === 'dark';
    },
    _resolveChartTextColor(isDark) {
      // Dark mode: read --gray-30 from the design tokens so charts use the
      // same soft-gray palette as the rest of the app (visible but softer
      // than pure white). Hardcoded fallback if the token isn't loaded.
      // Light mode: return Chart.js's built-in default ('#666'), used to
      // restore Chart.defaults.color when switching back from dark.
      if (!isDark) return '#666';
      const tokenValue = getComputedStyle(document.documentElement)
        .getPropertyValue('--gray-30').trim();
      return tokenValue || '#C8CDD8';
    },
    _resolveChartGridColor() {
      // Only called from dark-mode theming. Uses --gray-60 (#676D78) so
      // gridlines are visible against the dark background without competing
      // with the brighter --gray-30 text labels above them.
      const tokenValue = getComputedStyle(document.documentElement)
        .getPropertyValue('--gray-60').trim();
      return tokenValue || '#676D78';
    },
    _themeChartOptions(options, textColor, labelFontWeight) {
      // Mutates `options` in place to apply dark-mode chart text overrides.
      // Caller is responsible for deciding whether to invoke this — in light
      // mode we skip it entirely so the backend's original colors stand.
      if (!options) return;
      const gridColor = this._resolveChartGridColor();

      options.color = textColor;
      options.font = options.font || {};
      options.font.weight = options.font.weight || labelFontWeight;

      // ----- Plugins (legend / title / subtitle) -----
      options.plugins = options.plugins || {};

      options.plugins.legend = options.plugins.legend || {};
      options.plugins.legend.labels = options.plugins.legend.labels || {};
      options.plugins.legend.labels.color = textColor;
      options.plugins.legend.labels.font = options.plugins.legend.labels.font || {};
      options.plugins.legend.labels.font.weight = labelFontWeight;

      if (options.plugins.title) {
        options.plugins.title.color = textColor;
        options.plugins.title.font = options.plugins.title.font || {};
        options.plugins.title.font.weight = options.plugins.title.font.weight || 700;
      }
      if (options.plugins.subtitle) {
        options.plugins.subtitle.color = textColor;
        options.plugins.subtitle.font = options.plugins.subtitle.font || {};
        options.plugins.subtitle.font.weight = options.plugins.subtitle.font.weight || labelFontWeight;
      }

      // ----- Scales (Cartesian ticks/titles + radial point labels) -----
      // Note: most backend configs omit options.scales entirely — Chart.js
      // then auto-creates default scales from Chart.defaults.scale, which is
      // handled in _pushChartGlobalDefaults. This loop is here to override
      // any per-chart scale config the backend *does* supply.
      if (options.scales && typeof options.scales === 'object') {
        for (const key of Object.keys(options.scales)) {
          const scale = options.scales[key];
          if (!scale || typeof scale !== 'object') continue;

          scale.ticks = scale.ticks || {};
          scale.ticks.color = textColor;
          scale.ticks.font = scale.ticks.font || {};
          scale.ticks.font.weight = scale.ticks.font.weight || labelFontWeight;

          if (scale.title) {
            scale.title.color = textColor;
            scale.title.font = scale.title.font || {};
            scale.title.font.weight = scale.title.font.weight || 700;
          }

          // Radar / polarArea charts use a radial scale (`r`) whose category
          // labels live under `pointLabels`, not `ticks`.
          if (scale.pointLabels) {
            scale.pointLabels.color = textColor;
            scale.pointLabels.font = scale.pointLabels.font || {};
            scale.pointLabels.font.weight = scale.pointLabels.font.weight || labelFontWeight;
          }

          // Gridlines + axis border. Chart.js v3 keeps the axis line color on
          // `grid.borderColor`; v4 moved it to a separate `border.color`. Set
          // both so we cover whichever version `window.Chart` resolves to.
          scale.grid = scale.grid || {};
          scale.grid.color = gridColor;
          scale.grid.borderColor = gridColor;
          scale.border = scale.border || {};
          scale.border.color = gridColor;

          // Radar charts also draw spoke lines from center to each vertex.
          if (scale.angleLines) {
            scale.angleLines.color = gridColor;
          }
        }
      }
    },
    retintInitializedCharts() {
      // Called when the store's theme changes. Rebuilds each chart from its
      // pristine config snapshot, applying dark-mode overrides only when
      // we're actually in dark mode. Destroy + recreate (rather than mutate +
      // update) because Chart.js v4 normalizes scale options at construction
      // and live mutations don't reliably reach the per-scale option cache.
      if (!window.Chart || typeof window.Chart.getChart !== 'function') return;

      const isDark = this._isDarkTheme();
      const textColor = this._resolveChartTextColor(isDark);
      const labelFontWeight = 600;

      // Keep Chart.defaults in sync so any chart created after this point
      // (e.g. a brand-new Rey reply) also picks up the right baseline color.
      // This is also the *only* override path for charts whose backend config
      // omits options.scales — Chart.js auto-creates scales from defaults.
      this._pushChartGlobalDefaults(isDark, textColor);

      this.initialized_charts.forEach(chartId => {
        const chart = window.Chart.getChart(chartId);
        if (!chart) return;
        const canvas = chart.canvas;
        if (!canvas) return;

        // Prefer the pristine snapshot so light mode truly reverts to the
        // backend's original colors. Fall back to the live config for charts
        // created before this snapshot logic shipped — they'll keep whatever
        // colors were last applied, but a page reload fixes them up.
        const pristineJson = this.chart_original_configs[chartId];
        const freshConfig = pristineJson
          ? JSON.parse(pristineJson)
          : chart.config;

        if (isDark)
          this._themeChartOptions(freshConfig.options, textColor, labelFontWeight);

        chart.destroy();
        // eslint-disable-next-line no-new
        new window.Chart(canvas.getContext('2d'), freshConfig);
      });
    },
    hasUsefulChartData(chartConfig) {
      if (!chartConfig?.data?.datasets || !Array.isArray(chartConfig.data.datasets))
        return false;
      
      const chartType = chartConfig.type?.toLowerCase();
      for (const dataset of chartConfig.data.datasets) {
        if (!dataset.data || !Array.isArray(dataset.data))
          continue;
        
        // Check based on chart type
        switch (chartType) {
          case 'bar':
          case 'line':
          case 'scatter':
            if (this.hasUsefulXYData(dataset.data))
              return true;
            break;
          case 'pie':
          case 'doughnut':
            if (this.hasUsefulPieData(dataset.data))
              return true;
            break;
          default:
            // For unknown chart types, try both approaches
            if (this.hasUsefulXYData(dataset.data) || this.hasUsefulPieData(dataset.data))
              return true;
        }
      }
      
      return false;
    },
    hasUsefulXYData(data) {
      const values = [];
      
      for (const item of data) {
        let value;
        
        if (typeof item === 'object' && item !== null)
          value = item.y !== undefined ? item.y : item.value;
        else
          value = item;
        
        if (typeof value === 'number' && !isNaN(value))
          values.push(value);
      }
      
      return this.hasVariationInValues(values);
    },
    hasUsefulPieData(data) {
      const values = data.filter(item =>
        typeof item === 'number' && !isNaN(item) && item > 0
      );
      
      // Pie charts need at least one positive value
      return values.length > 0 && values.some(val => val > 0);
    },
    hasVariationInValues(values) {
      if (values.length === 0) return false;
      
      // Check if all values are the same (including all zeros)
      const firstValue = values[0];
      const allSame = values.every(val => val === firstValue);
      
      if (allSame)
        return firstValue !== 0;
      
      return true;
    },
    generateTableHtml(tableData, chartId) {
      // Generate HTML table from tableData { columns: [...], rows: [[...], [...]] }
      if (!tableData || !tableData.columns || !tableData.rows) {
        console.warn(`[MessageItem] Invalid tableData for chart ${chartId}`);
        return '';
      }
      
      const columns = tableData.columns;
      const rows = tableData.rows;
      
      // Build header
      const headerCells = columns.map(col => `<th>${this.escapeHtml(col)}</th>`).join('');
      const headerRow = `<thead><tr>${headerCells}</tr></thead>`;
      
      // Build body
      const bodyRows = rows.map(row => {
        const cells = row.map(cell => `<td>${this.escapeHtml(String(cell ?? ''))}</td>`).join('');
        return `<tr>${cells}</tr>`;
      }).join('');
      const tableBody = `<tbody>${bodyRows}</tbody>`;
      
      return `<table class="avery-table" id="table-${chartId}">${headerRow}${tableBody}</table>`;
    },
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },
    replyToMessage() {
      this.$emit('replyToMessage', this.messageReply.senderId);
    },
    startStreamingTypingEffect() {
      const self = this;
      if (!this.streamingDisplayBuffer || this.is_typing) return;
      
      this.is_typing = true;
      let streamingSpeed = this.streamingDisplayBuffer.isComplete?this.streaming_done_typing_speed:this.streaming_typing_speed;
      this.streaming_interval = setInterval(() => {
        self.releaseBufferedCharacters();
      }, streamingSpeed);
    },
    startReplyStreamingTypingEffect() {
      const self = this;
      if (!this.replyStreamingDisplayBuffer || this.is_typing_reply) return;
      
      this.is_typing_reply = true;
      let streamingSpeed = this.replyStreamingDisplayBuffer.isComplete?this.streaming_done_typing_speed:this.streaming_typing_speed;
      this.streaming_reply_interval = setInterval(() => {
        self.releaseBufferedCharactersForReply();
      }, streamingSpeed);
    },
    releaseBufferedCharacters() {
      const self = this;
      const bufferData = this.streamingDisplayBuffer;
      if (!bufferData) return;
      
      if(this.pause_typing !== true || bufferData.isComplete) {
        const remainingChars = bufferData.buffer.length - this.streaming_buffer_displayed.length;
        if (remainingChars > 0) {
          let charsToAdd = this.getSmartCharsToAdd(bufferData.buffer, this.streaming_buffer_displayed, bufferData.isComplete, false);
          charsToAdd = Math.min(charsToAdd, remainingChars);
          this.streaming_buffer_displayed += bufferData.buffer.substring(this.streaming_buffer_displayed.length, this.streaming_buffer_displayed.length + charsToAdd);
          this.$emit('updateStreamingDisplay', this.message.id, this.streaming_buffer_displayed);
        }
        
        // Check for streaming completion
        if (bufferData.isComplete && this.streaming_buffer_displayed.length === bufferData.buffer.length) {
          // Stream has ended and all content is displayed - cleanup
          this.stopStreamingTypingEffect();
          
          // Stable Chart Initialization: Only once it's fully typed out
          const hasChartArtifact = this.message.artifacts?.some(artifact => artifact.type === 'Chart' || artifact.type === 0);
          console.log('[MessageItem] Streaming complete check:', { isComplete: bufferData.isComplete, displayedLen: this.streaming_buffer_displayed.length, bufferLen: bufferData.buffer.length, hasChartArtifact, artifacts: this.message.artifacts });
          if (hasChartArtifact) {
            setTimeout(() => {
              self.initMessageChart(self.message);
            }, 100);
          }
        }
      }
    },
    releaseBufferedCharactersForReply() {
      const self = this;
      const bufferData = this.replyStreamingDisplayBuffer;
      if (!bufferData) return;
      
      if(this.pause_typing_reply !== true || bufferData.isComplete) {
        const remainingChars = bufferData.buffer.length - this.streaming_reply_buffer_displayed.length;
        if (remainingChars > 0) {
          let charsToAdd = this.getSmartCharsToAdd(bufferData.buffer, this.streaming_reply_buffer_displayed, bufferData.isComplete, true);
          charsToAdd = Math.min(charsToAdd, remainingChars);
          this.streaming_reply_buffer_displayed += bufferData.buffer.substring(this.streaming_reply_buffer_displayed.length, this.streaming_reply_buffer_displayed.length + charsToAdd);
        }

        // Check for streaming completion
        if (bufferData.isComplete && this.streaming_reply_buffer_displayed.length === bufferData.buffer.length) {
          // Stream has ended - cleanup
          this.stopReplyStreamingTypingEffect();

          // Stable Chart Initialization for replies
          const hasChartArtifact = this.messageReply.artifacts?.some(artifact => artifact.type === 'Chart');
          if (hasChartArtifact) {
            this.$nextTick(() => {
              self.$nextTick(() => {
                self.initMessageChart(self.messageReply);
              });
            });
          }
        }
      }
    },
    stopStreamingTypingEffect() {
      const bufferData = this.streamingDisplayBuffer;
      if (!bufferData) return;
      
      if (this.streaming_interval) {
        clearInterval(this.streaming_interval);
        this.streaming_interval = null;
      }
      this.is_typing = false;
      
      if (this.streaming_buffer_displayed !== bufferData.buffer) {
        this.streaming_buffer_displayed = bufferData.buffer;
        this.$emit('updateStreamingDisplay', this.message.id, this.streaming_buffer_displayed);
      }
      
      // Notify parent that streaming is complete
      this.$emit('streamingComplete', this.message.id);

      if (this.$FUSarrayHasEntries(this.message.suggestions))
        this.fireSuggestionShownEvents(this.message.suggestions);
    },
    stopReplyStreamingTypingEffect() {
      const bufferData = this.replyStreamingDisplayBuffer;
      if (!bufferData) return;
      
      if (this.streaming_reply_interval) {
        clearInterval(this.streaming_reply_interval);
        this.streaming_reply_interval = null;
      }
      this.is_typing_reply = false;
      
      if (this.streaming_reply_buffer_displayed !== bufferData.buffer) {
        this.streaming_reply_buffer_displayed = bufferData.buffer;
        this.$emit('updateStreamingDisplay', this.messageReply.id, this.streaming_reply_buffer_displayed);
      }
      
      // Notify parent that streaming is complete
      this.$emit('streamingComplete', this.messageReply.id);
    },
    getSmartCharsToAdd(bufferedData='', displayedData='', isComplete=false, isReplyBuffer=false) {
      const self = this;
      const currentPos = displayedData.length;
      const remainingBuffer = bufferedData.substring(currentPos);
      const maxChars = isComplete?this.streaming_done_chars_per_tick:this.streaming_chars_per_tick;
      let pauseTyping = isReplyBuffer?this.pause_typing_reply:this.pause_typing;
      
      if (!remainingBuffer) return 0;
      
      // Handle markdown tables
      if (this.isLikelyTableContent(displayedData, remainingBuffer, 100)) {
        let charsToAddForTable = this.handleTableStreaming(remainingBuffer, maxChars);
        if(charsToAddForTable<0 && pauseTyping===null) {
          if(isReplyBuffer)
            this.pause_typing_reply = true;
          else
            this.pause_typing = true;
          setTimeout(() => {
            if(pauseTyping) {
              if(isReplyBuffer)
                self.pause_typing_reply = false;
              else
                self.pause_typing = false;
            }
          }, 1000);
          return 0;
        }
        else if(charsToAddForTable<0 && pauseTyping===false)
          return maxChars;
        
        return Math.max(charsToAddForTable, maxChars); // Ensure we don't slow down below maxChars if table logic returned small value
      }
      
      // Markdown Look-ahead for bold/italic/code-inline
      const lookAheadRange = remainingBuffer.substring(0, 50);
      const markdownMarkers = [
        { pattern: /^\*\*.*?\*\*/, length: null },
        { pattern: /^__.*?__/, length: null },
        { pattern: /^`.*?`/, length: null },
        { pattern: /^\*.*?\*/, length: null },
        { pattern: /^_.*?_/, length: null }
      ];

      for (const marker of markdownMarkers) {
        const match = lookAheadRange.match(marker.pattern);
        if (match) {
          return match[0].length;
        }
      }

      // Look ahead for code blocks
      if (remainingBuffer.startsWith('```')) {
        const endIndex = remainingBuffer.indexOf('```', 3);
        if (endIndex !== -1)
          return endIndex + 3;
        return 3;
      }
      
      // Check for HTML tags and entities
      for (let i = 0; i < Math.min(maxChars, remainingBuffer.length); i++) {
        const char = remainingBuffer[i];
        
        if (char === '<')
          return this.handleHtmlTagStart(remainingBuffer, i, maxChars);
        
        if (char === '&') {
          let entityStart = this.handleHtmlEntityStart(remainingBuffer, i);
          return entityStart===null?maxChars:entityStart;
        }
      }
      
      return this.getVariableCharsPerTick(bufferedData, displayedData, isComplete, currentPos, maxChars);
    },
    isLikelyTableContent(displayed, remaining, maxChars) {
      const recentContent = displayed.slice(-100);
      const upcomingContent = remaining.slice(0, maxChars);
      
      const tablePatterns = [
        /\|[^|]*$/,
        /^\s*\|/,
        /\|\s*[-:]+\s*\|/,
        /\n\s*\|/,
      ];
      
      return tablePatterns.some(pattern =>
        pattern.test(recentContent) || pattern.test(upcomingContent)
      );
    },
    handleTableStreaming(remainingBuffer, maxChars) {
      if (remainingBuffer.startsWith('|')) {
        const rowEnd = this.findTableRowEnd(remainingBuffer);
        if (rowEnd === -1)
          return -1;
        else
          return Math.min(rowEnd, remainingBuffer.length);
      }
      
      const nextPipe = remainingBuffer.indexOf('|');
      if (nextPipe !== -1 && nextPipe < 30) // Look ahead up to 30 chars to jump to the start of a table
        return nextPipe;
      
      if (remainingBuffer.match(/^\s*[-:]+\s*\|/)) {
        const separatorEnd = this.findTableSeparatorEnd(remainingBuffer);
        if (separatorEnd !== -1)
          return separatorEnd;
      }
      
      return maxChars;
    },
    findTableRowEnd(buffer) {
      let pipeCount = 0;
      let inCodeBlock = false;
      
      for (let i = 0; i < buffer.length; i++) {
        const char = buffer[i];
        
        if (char === '`')
          inCodeBlock = !inCodeBlock;
        
        if (char === '|' && !inCodeBlock)
          pipeCount++;
        
        if (char === '\n' && pipeCount >= 2) {
          let bufferNext = buffer.substring(i+1);
          if (bufferNext.match(/^\s*\|?\s*:?-+:?\s*\|/)) {
            const separatorEnd = this.findTableSeparatorEnd(bufferNext);
            if (separatorEnd !== -1)
              return separatorEnd + i + 1;
          }
          return i + 1;
        }
        
        if (i === buffer.length - 1 && pipeCount >= 2)
          return i + 1;
      }
      
      return -1;
    },
    findTableSeparatorEnd(buffer) {
      const match = buffer.match(/^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*\n?/);
      if(match) {
        const hasValidSeparator = /-{1,}/.test(match[0]);
        if(hasValidSeparator)
          return match[0].length;
      }
      return -1;
    },
    handleHtmlTagStart(remainingBuffer, position, maxChars) {
      if (position === 0) {
        // Only treat as HTML if '<' is followed by a valid tag-start character (letter, '/', or '!')
        // This prevents '<' used as a less-than operator from stalling the typewriter loop
        if (!/^<[a-zA-Z\/!]/.test(remainingBuffer))
          return maxChars;
        const tagMatch = remainingBuffer.match(/^<[^>]*>/);
        if (tagMatch)
          return tagMatch[0].length;
      }
      return position;
    },
    handleHtmlEntityStart(remainingBuffer, position) {
      const entityFromHere = remainingBuffer.substring(position);
      const entityMatch = entityFromHere.match(/^&([a-zA-Z]+|#[0-9]+|#x[0-9a-fA-F]+);/);
      
      if (entityMatch) {
        if (position === 0)
          return entityMatch[0].length;
        return position;
      }
      return null;
    },
    getVariableCharsPerTick(bufferedData='', displayedData='', isComplete=false, currentPos, maxChars) {
      const ahead = bufferedData.length - currentPos;

      // Dynamic typing speed: pace the typewriter so it finishes the current
      // buffer roughly when the next block arrives (~1s). Instead of typing
      // everything as fast as possible, aim for a steady rate that keeps text
      // appearing smoothly across block boundaries.
      // Target: drain the buffer in ~800ms (leaving headroom before next block).
      // ticksAvailable = 800ms / interval (30ms) ≈ 26 ticks
      // charsPerTick = ahead / 26, clamped to [1, maxPerTick]
      const intervalMs = isComplete ? this.streaming_done_typing_speed : this.streaming_typing_speed;
      const targetDrainMs = isComplete ? 400 : 800;
      const ticksAvailable = Math.max(1, Math.floor(targetDrainMs / intervalMs));
      const maxPerTick = isComplete ? 20 : 12;
      const chars = Math.min(Math.max(1, Math.ceil(ahead / ticksAvailable)), ahead, maxPerTick);

      const lastDisplayedChar = displayedData.slice(-1);
      const nextChars = bufferedData.substring(currentPos, currentPos + 3);

      if (['.', '!', '?'].includes(lastDisplayedChar))
        return 1;

      if ([',', ';', ':'].includes(lastDisplayedChar))
        return 1;

      if (lastDisplayedChar === ' ' || nextChars.match(/^(the|and|of|to|a|in|for|is|on|that|by|this|with|i|you|it|not|or|be|are|from|at|as|your|all|any|can|had|her|was|one|our|out|day|get|has|him|his|how|man|new|now|old|see|two|way|who|boy|did|its|let|put|say|she|too|use)/i))
        return Math.min(chars, 8);

      return chars;
    },
    getSenderImage(senderId) {
      if(senderId === 'system_TACCHAT')
        return this.tac_chat_logo;
      
      return senderId && senderId.indexOf(this.$FUSsystemUserPrefix)>=0?this.spark_icon:null;
    },
  },
  watch: {
    current_theme() {
      // _applyTheme has already updated data-theme on the document root by
      // the time this watcher fires, so getComputedStyle picks up the new
      // value of --text-default. Wait one tick to be safe in case Vue batched.
      this.$nextTick(() => this.retintInitializedCharts());
    },
    messageFeedbackStatus(newStatus, oldStatus) {
      // When the user switches away from ThumbsDown (to ThumbsUp, or back to unrated), the persisted comment
      // is conceptually irrelevant. Close the editor and reset draft so the next ThumbsDown starts clean.
      // Server clears the persisted Comment naturally on the next SubmitFeedback (the request DTO ships with
      // comment=''), so this is purely a UI reset.
      if (oldStatus === 'ThumbsDown' && newStatus !== 'ThumbsDown') {
        this.comment_box_open = false;
        this.comment_draft = '';
        this.comment_thank_you_visible = false;
        this.comment_saved_since_open = false;
        if (this.comment_thank_you_timer) {
          clearTimeout(this.comment_thank_you_timer);
          this.comment_thank_you_timer = null;
        }
      }
    },
    messageComment(newComment) {
      // Keep the draft in sync with the persisted value EXCEPT while the user is actively typing — that's
      // detected by whether the textarea is currently focused. Without this guard, an inbound WebPubSub
      // broadcast (or hydration response) arriving mid-keystroke would clobber what the user is typing.
      const isEditing = this.$refs.comment_input && document.activeElement === this.$refs.comment_input;
      if (!isEditing) this.comment_draft = newComment || '';
    },
    'message.suggestions'(newSuggestions) {
      if (!this.isStreamingMessage && !this.is_typing && newSuggestions?.length) {
        this.fireSuggestionShownEvents(newSuggestions);
      }
    },
    isStreamingMessage(newVal, oldVal) {
      if (!newVal && oldVal && !this.is_typing && this.$FUSarrayHasEntries(this.message.suggestions)) {
        this.fireSuggestionShownEvents(this.message.suggestions);
      }
    },
    chart_artifacts: {
      handler(newArtifacts, oldArtifacts) {
        console.log('[CHART_DEBUG] chart_artifacts watcher fired', {
          newCount: newArtifacts?.length || 0,
          oldCount: oldArtifacts?.length || 0,
          newTypes: newArtifacts?.map(a => ({ id: a.id, type: a.type, typeType: typeof a.type })),
          renderedChartsCount: this.rendered_charts.length,
          msgId: this.message.id,
          hasArtifactsProp: this.$FUSarrayHasEntries(this.message.artifacts),
          artifactsProp: this.message.artifacts?.map(a => ({ id: a.id, type: a.type, typeType: typeof a.type }))
        });
        if (this.$FUSarrayHasEntries(newArtifacts)) {
          // Merge new charts into rendered_charts (never remove existing ones)
          let changed = false;
          for (let artifact of newArtifacts) {
            if (!this.rendered_charts.find(a => a.id === artifact.id)) {
              this.rendered_charts.push(artifact);
              changed = true;
            }
          }
          if (changed) {
            const self = this;
            // Snapshot the artifacts — this.message.artifacts may be cleared by a
            // subsequent WS message before this timeout fires.
            const artifactsSnapshot = newArtifacts.map(a => ({ ...a }));
            this.$nextTick(() => {
              setTimeout(() => {
                console.log('[CHART_DEBUG] initMessageChart from watcher', {
                  chartId: artifactsSnapshot[0]?.id,
                  renderedChartsCount: self.rendered_charts.length
                });
                self.initMessageChart({ artifacts: artifactsSnapshot });
              }, 100);
            });
          }
        }
      },
      deep: true
    },
    streamingDisplayBuffer(newVal, oldVal) {
      // When the streaming buffer is cleared externally (e.g., FactualityVerified
      // replacement), stop the typewriter interval to prevent leaks.
      if (!newVal && oldVal && this.is_typing) {
        if (this.streaming_interval) {
          clearInterval(this.streaming_interval);
          this.streaming_interval = null;
        }
        this.is_typing = false;
      }
      // When a NEW buffer arrives after the old one was DELETED (the
      // FactualityVerifying → FactualityVerified transition: Verifying deletes
      // the buffer, then Verified creates a fresh one), oldVal is undefined so
      // the newVal && oldVal branch below doesn't fire. Without this reset,
      // streaming_buffer_displayed retains stale preamble/narration text and
      // the typewriter starts at the wrong offset — or never types at all if
      // the stale text is longer than the new answer.
      if (newVal && !oldVal && this.streaming_buffer_displayed) {
        console.log('[BUFFER_RECREATE]', {
          msgId: this.message.id,
          displayedLen: this.streaming_buffer_displayed.length,
          newBufferLen: newVal.buffer?.length,
          newPreview: newVal.buffer?.substring(0, 40)
        });
        this.streaming_buffer_displayed = '';
        this.$emit('updateStreamingDisplay', this.message.id, '');
        if (this.streaming_interval) {
          clearInterval(this.streaming_interval);
          this.streaming_interval = null;
        }
        this.is_typing = false;
        this.startStreamingTypingEffect();
      }
      // When a NEW buffer with DIFFERENT CONTENT arrives (preamble → verified
      // replacement), reset the typewriter position and restart so it types
      // the new content. Skip when only isComplete changed (streamEnd replaces
      // the buffer object to set isComplete=true, but content is the same —
      // resetting would type the entire answer twice).
      if (newVal && oldVal && newVal !== oldVal
          && newVal.buffer !== oldVal.buffer) {
        console.log('[BUFFER_REPLACE]', {
          msgId: this.message.id,
          oldLen: oldVal.buffer?.length,
          newLen: newVal.buffer?.length,
          oldIsComplete: oldVal.isComplete,
          newIsComplete: newVal.isComplete,
          oldPreview: oldVal.buffer?.substring(0, 40),
          newPreview: newVal.buffer?.substring(0, 40),
          wasTyping: this.is_typing
        });
        this.streaming_buffer_displayed = '';
        this.$emit('updateStreamingDisplay', this.message.id, '');
        if (this.streaming_interval) {
          clearInterval(this.streaming_interval);
          this.streaming_interval = null;
        }
        this.is_typing = false;
        this.startStreamingTypingEffect();
      }
    },
    'streamingDisplayBuffer.buffer': {
      handler(newBuffer, oldBuffer) {
        // Start streaming when buffer content arrives (if not already typing)
        if (newBuffer && !oldBuffer && this.streamingDisplayBuffer && !this.is_typing)
          this.startStreamingTypingEffect();
        this.pause_typing = null
      },
      immediate: false
    },
    'replyStreamingDisplayBuffer.buffer': {
      handler(newBuffer, oldBuffer) {
        // Start streaming for reply when buffer content arrives
        if (newBuffer && !oldBuffer && this.replyStreamingDisplayBuffer && !this.is_typing_reply)
          console.log('[MessageItem] Reply streaming buffer detected, starting streaming for reply');
          this.startReplyStreamingTypingEffect();
        this.pause_typing_reply = null
      },
      immediate: false
    }
  },
}
</script>
